import { useState, useRef, useEffect, useCallback } from "react";
import Chart from "chart.js/auto";
import useTheme from "../components/useTheme";
import { ReactComponent as Logo } from '../assets/kcg.svg';
import { ReactComponent as Logon } from '../assets/kcgn.svg';
import { ReactComponent as Logout } from '../assets/logout.svg';
import { ReactComponent as Mongodb } from '../assets/mongodb.svg';
import { ReactComponent as Nodejs } from '../assets/nodejs.svg';
import { ReactComponent as Python } from '../assets/python.svg';
import { ReactComponent as Reacti } from '../assets/react.svg';
import { ReactComponent as Github } from '../assets/github.svg';
import { ReactComponent as Linkedin } from '../assets/linkedin.svg';
import { ReactComponent as Docker } from '../assets/docker.svg';

const STARS_COUNT = 130;
const PARTICLES_COUNT = 18;

function Stars() {
  const stars = useRef(
    Array.from({ length: STARS_COUNT }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 2.4 + 0.5,
      delay: Math.random() * 6,
      duration: Math.random() * 2 + 2,
      d: 2 + Math.random() * 4,
      dl: Math.random() * 6,
    }))
  ).current;

  return (
    <div className="stars">
      {stars.map((s, i) => (
        <div
          key={i}
          className="star"
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            "--d": `${s.d}s`,
            "--dl": `${s.dl}s`,
          }}
        />
      ))}
    </div>
  );
}

function Particles() {
  const particles = useRef(
    Array.from({ length: PARTICLES_COUNT }, (_, i) => ({
      id: i,
      size: Math.random() * 6 + 3,
      left: Math.random() * 100,
      bottom: Math.random() * 40,
      duration: Math.random() * 8 + 6,
      delay: Math.random() * 8,
      pd: Math.random() * 8 + 6,
      pdl: Math.random() * 8,
    }))
  ).current;

  return (
    <div className="particles">
      {particles.map((p) => (
        <div
          key={p.id}
          className="particle"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            bottom: `${p.bottom}%`,
            left: `${p.left}%`,
            background: "rgba(93,190,106,0.3)",
            "--pd": `${p.pd}s`,
            "--pdl": `${p.pdl}s`,
          }}
        />
      ))}
    </div>
  );
}

function Clouds() {
  const clouds = [
    { top: "6%", w: "190px", h: "55px", cd: "32s", cdl: "0s" },
    { top: "15%", w: "130px", h: "40px", cd: "48s", cdl: "-18s" },
    { top: "4%", w: "220px", h: "65px", cd: "58s", cdl: "-28s" },
    { top: "22%", w: "100px", h: "32px", cd: "38s", cdl: "-8s" },
  ];

  return (
    <div className="cloud-layer">
      {clouds.map((c, i) => (
        <div
          key={i}
          className="cloud"
          style={{
            top: c.top,
            width: c.w,
            height: c.h,
            "--cd": c.cd,
            "--cdl": c.cdl,
            left: "-15%",
          }}
        />
      ))}
    </div>
  );
}

const GRADES_LIST = ["O", "A+", "A", "B+", "B", "C", "F"];
const GRADE_PTS = { O: 10, "A+": 9, A: 8, "B+": 7, B: 6, C: 5, F: 0 };
const SIM_GRADES = ["F", "C", "B", "B+", "A", "A+", "O"];

// Helper function to convert numbers to ordinal (1st, 2nd, 3rd, etc.)
function getOrdinal(num) {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return num + "st";
  if (j === 2 && k !== 12) return num + "nd";
  if (j === 3 && k !== 13) return num + "rd";
  return num + "th";
}

const DATA = {
  1: {
    label: "1st",
    credits: 22,
    gpa: 8.2,
    subjects: [
      { code: "C101", name: "Engineering Maths", cr: 4, grade: "A+" },
      { code: "C102", name: "Physics", cr: 3, grade: "A" },
      { code: "C103", name: "Chemistry", cr: 3, grade: "B+" },
      { code: "C104", name: "C Programming", cr: 4, grade: "O" },
      { code: "C105", name: "Communication", cr: 2, grade: "A" },
    ],
    contrib: [
      { g: 85, o: 10, r: 5, val: 4.2 },
      { g: 70, o: 20, r: 10, val: 3.8 },
      { g: 55, o: 30, r: 15, val: 3.0 },
      { g: 90, o: 8, r: 2, val: 4.5 },
      { g: 62, o: 23, r: 15, val: 3.2 },
    ],
    risk: [{ name: "Chemistry", grade: "B+", lvl: "warn" }],
  },
  2: {
    label: "2nd",
    credits: 22,
    gpa: 8.4,
    subjects: [
      { code: "C201", name: "Discrete Maths", cr: 4, grade: "A" },
      { code: "C202", name: "Digital Electronics", cr: 3, grade: "A+" },
      { code: "C203", name: "Data Structures", cr: 4, grade: "O" },
      { code: "C204", name: "Computer Networks", cr: 3, grade: "B+" },
      { code: "C205", name: "OOP with Java", cr: 4, grade: "A" },
    ],
    contrib: [
      { g: 72, o: 18, r: 10, val: 3.9 },
      { g: 85, o: 10, r: 5, val: 4.3 },
      { g: 90, o: 8, r: 2, val: 4.6 },
      { g: 56, o: 29, r: 15, val: 3.1 },
      { g: 72, o: 18, r: 10, val: 3.8 },
    ],
    risk: [{ name: "Computer Networks", grade: "B+", lvl: "warn" }],
  },
  3: {
    label: "3rd",
    credits: 24,
    gpa: 7.65,
    subjects: [
      { code: "M301", name: "Operating Systems", cr: 4, grade: "B" },
      { code: "M302", name: "Microprocessors", cr: 3, grade: "C" },
      { code: "M303", name: "Software Engineering", cr: 3, grade: "B+" },
      { code: "M304", name: "Database Systems", cr: 4, grade: "B" },
      { code: "M305", name: "TOC", cr: 3, grade: "C" },
    ],
    contrib: [
      { g: 45, o: 30, r: 25, val: 2.8 },
      { g: 28, o: 32, r: 40, val: 1.9 },
      { g: 60, o: 25, r: 15, val: 3.2 },
      { g: 46, o: 32, r: 22, val: 2.6 },
      { g: 26, o: 32, r: 42, val: 1.8 },
    ],
    risk: [
      { name: "Microprocessors", grade: "C", lvl: "danger" },
      { name: "TOC", grade: "C", lvl: "danger" },
      { name: "Operating Systems", grade: "B", lvl: "warn" },
    ],
  },
  4: {
    label: "4th",
    credits: 24,
    gpa: 8.3,
    subjects: [
      { code: "M401", name: "Algorithms", cr: 4, grade: "A" },
      { code: "M402", name: "Computer Architecture", cr: 3, grade: "B+" },
      { code: "M403", name: "AI Fundamentals", cr: 3, grade: "A+" },
      { code: "M404", name: "Mobile Computing", cr: 3, grade: "A" },
      { code: "M405", name: "Web Technologies", cr: 4, grade: "B+" },
    ],
    contrib: [
      { g: 72, o: 18, r: 10, val: 4.0 },
      { g: 58, o: 28, r: 14, val: 3.3 },
      { g: 84, o: 10, r: 6, val: 4.4 },
      { g: 74, o: 16, r: 10, val: 3.8 },
      { g: 60, o: 25, r: 15, val: 3.5 },
    ],
    risk: [{ name: "Computer Architecture", grade: "B+", lvl: "warn" }],
  },
  5: {
    label: "5th",
    credits: 24,
    gpa: 9.29,
    subjects: [
      { code: "M501", name: "Data Structures", cr: 4, grade: "O" },
      { code: "M502", name: "Operating Systems", cr: 4, grade: "A" },
      { code: "M503", name: "Algorithms", cr: 4, grade: "A+" },
      { code: "M504", name: "Database Management", cr: 3, grade: "B+" },
      { code: "M505", name: "Flight Dynamics", cr: 3, grade: "B" },
    ],
    contrib: [
      { g: 90, o: 8, r: 2, val: 4.5 },
      { g: 70, o: 20, r: 10, val: 3.9 },
      { g: 80, o: 14, r: 6, val: 4.2 },
      { g: 54, o: 29, r: 17, val: 3.0 },
      { g: 28, o: 35, r: 37, val: 1.7 },
    ],
    risk: [
      { name: "Flight Dynamics", grade: "B", lvl: "danger" },
      { name: "Database Management", grade: "B+", lvl: "warn" },
    ],
  },
};

const INITIAL_DATA = DATA; // kept so EditModal can seed rows
const ALL_GPA = [8.2, 8.4, 7.65, 8.3, 9.29];
const ALL_CGPA = [6.5, 7.0, 7.2, 7.5, 7.8];

const LB_DATA = [
  { name: "Priya Suresh", reg: "20KCCE002", department: "CSE", studentClass: "A", gpa: { 1: 9.1, 2: 9.3, 3: 9.4, 4: 9.5, 5: 9.72 }, cgpa: 9.41 },
  { name: "Karthik Rajan", reg: "20KCCE005", department: "CSE", studentClass: "B", gpa: { 1: 9.0, 2: 9.1, 3: 9.2, 4: 9.4, 5: 9.55 }, cgpa: 9.25 },
  { name: "John Doe", reg: "20KCCE001", department: "CSE", studentClass: "A", gpa: { 1: 8.2, 2: 8.4, 3: 8.9, 4: 9.0, 5: 9.29 }, cgpa: 8.76, me: true },
  { name: "Deepa Murugan", reg: "20KCCE008", department: "ECE", studentClass: "C", gpa: { 1: 8.8, 2: 8.9, 3: 8.7, 4: 9.1, 5: 9.2 }, cgpa: 8.96 },
  { name: "Arun Anand", reg: "20KCCE011", department: "ME", studentClass: "B", gpa: { 1: 8.5, 2: 8.6, 3: 8.8, 4: 8.9, 5: 9.1 }, cgpa: 8.76 },
  { name: "Meena Raj", reg: "20KCCE003", department: "CSE", studentClass: "C", gpa: { 1: 8.3, 2: 8.5, 3: 8.6, 4: 8.8, 5: 9.05 }, cgpa: 8.64 },
  { name: "Vijay Kumar", reg: "20KCCE014", department: "CIVIL", studentClass: "A", gpa: { 1: 7.8, 2: 8.0, 3: 8.4, 4: 8.7, 5: 8.95 }, cgpa: 8.38 },
  { name: "Lakshmi Devi", reg: "20KCCE007", department: "ECE", studentClass: "B", gpa: { 1: 8.0, 2: 8.2, 3: 8.3, 4: 8.5, 5: 8.8 }, cgpa: 8.36 },
  { name: "Sathish Babu", reg: "20KCCE019", department: "EEE", studentClass: "C", gpa: { 1: 7.5, 2: 7.8, 3: 8.0, 4: 8.4, 5: 8.7 }, cgpa: 8.08 },
  { name: "Anitha Selvam", reg: "20KCCE006", department: "CSE", studentClass: "A", gpa: { 1: 7.9, 2: 8.0, 3: 8.1, 4: 8.3, 5: 8.6 }, cgpa: 8.18 },
  { name: "Ravi Shankar", reg: "20KCCE012", department: "ME", studentClass: "B", gpa: { 1: 7.2, 2: 7.5, 3: 8.0, 4: 8.0, 5: 8.45 }, cgpa: 7.8 },
  { name: "Kavitha Arumugam", reg: "20KCCE009", department: "CIVIL", studentClass: "C", gpa: { 1: 7.6, 2: 7.8, 3: 7.9, 4: 8.1, 5: 8.3 }, cgpa: 7.94 },
  { name: "Bala Subramanian", reg: "20KCCE016", department: "EEE", studentClass: "A", gpa: { 1: 7.0, 2: 7.2, 3: 7.5, 4: 7.8, 5: 8.1 }, cgpa: 7.5 },
  { name: "Nisha Thomas", reg: "20KCCE020", department: "CSE", studentClass: "B", gpa: { 1: 7.3, 2: 7.4, 3: 7.6, 4: 7.9, 5: 7.95 }, cgpa: 7.64 },
  { name: "Gopal Venkat", reg: "20KCCE013", department: "ECE", studentClass: "C", gpa: { 1: 6.8, 2: 7.0, 3: 7.3, 4: 7.5, 5: 7.8 }, cgpa: 7.28 },
  { name: "Sindhu Krishnan", reg: "20KCCE004", department: "ME", studentClass: "A", gpa: { 1: 6.5, 2: 6.8, 3: 7.0, 4: 7.2, 5: 7.6 }, cgpa: 7.02 },
  { name: "Muthu Pandian", reg: "20KCCE017", department: "CIVIL", studentClass: "B", gpa: { 1: 6.3, 2: 6.5, 3: 6.8, 4: 7.0, 5: 7.35 }, cgpa: 6.78 },
  { name: "Revathi Nair", reg: "20KCCE010", department: "EEE", studentClass: "C", gpa: { 1: 6.0, 2: 6.2, 3: 6.5, 4: 6.8, 5: 7.1 }, cgpa: 6.5 },
  { name: "Suresh Pillai", reg: "20KCCE018", department: "CSE", studentClass: "A", gpa: { 1: 5.8, 2: 6.0, 3: 6.3, 4: 6.5, 5: 6.8 }, cgpa: 6.28 },
  { name: "Tamilarasi G.", reg: "20KCCE015", department: "ECE", studentClass: "B", gpa: { 1: 5.5, 2: 5.8, 3: 6.0, 4: 6.2, 5: 6.5 }, cgpa: 6.0 },
];

const MEDALS = ["🥇", "🥈", "🥉"];

// Department Leaderboard Data - Student leaderboard organized by department
const DEPT_LB_DATA = [
  { name: "Priya Suresh", reg: "20KCCE002", department: "CSE", studentClass: "A", cgpa: 9.41, rank: 1 },
  { name: "Deepa Murugan", reg: "20KCCE008", department: "ECE", studentClass: "C", cgpa: 8.96, rank: 2 },
  { name: "Karthik Rajan", reg: "20KCCE005", department: "CSE", studentClass: "B", cgpa: 9.25, rank: 3 },
  { name: "John Doe", reg: "20KCCE001", department: "CSE", studentClass: "A", cgpa: 8.76, rank: 4 },
  { name: "Arun Anand", reg: "20KCCE011", department: "ME", studentClass: "B", cgpa: 8.76, rank: 5 },
  { name: "Meena Raj", reg: "20KCCE003", department: "CSE", studentClass: "C", cgpa: 8.64, rank: 6 },
  { name: "Vijay Kumar", reg: "20KCCE014", department: "CIVIL", studentClass: "A", cgpa: 8.38, rank: 7 },
  { name: "Lakshmi Devi", reg: "20KCCE007", department: "ECE", studentClass: "B", cgpa: 8.36, rank: 8 },
  { name: "Sathish Babu", reg: "20KCCE019", department: "EEE", studentClass: "C", cgpa: 8.08, rank: 9 },
  { name: "Anitha Selvam", reg: "20KCCE006", department: "CSE", studentClass: "A", cgpa: 8.18, rank: 10 },
];

// College Leaderboard Data - Student leaderboard organized by college
const COLLEGE_LB_DATA = [
  { name: "Priya Suresh", reg: "20KCCE002", department: "CSE", cgpa: 9.41, rank: 1 },
  { name: "Deepa Murugan", reg: "20KCCE008", department: "ECE", cgpa: 8.96, rank: 2 },
  { name: "Karthik Rajan", reg: "20KCCE005", department: "CSE", cgpa: 9.25, rank: 3 },
  { name: "John Doe", reg: "20KCCE001", department: "CSE", cgpa: 8.76, rank: 4 },
  { name: "Arun Anand", reg: "20KCCE011", department: "ME", cgpa: 8.76, rank: 5 },
  { name: "Meena Raj", reg: "20KCCE003", department: "CSE", cgpa: 8.64, rank: 6 },
  { name: "Vijay Kumar", reg: "20KCCE014", department: "CIVIL", cgpa: 8.38, rank: 7 },
  { name: "Lakshmi Devi", reg: "20KCCE007", department: "ECE", cgpa: 8.36, rank: 8 },
  { name: "Sathish Babu", reg: "20KCCE019", department: "EEE", cgpa: 8.08, rank: 9 },
  { name: "Anitha Selvam", reg: "20KCCE006", department: "CSE", cgpa: 8.18, rank: 10 },
];

function gradeDist(sem, data = DATA) {
  const cnt = { O: 0, "A+": 0, A: 0, "B+": 0, B: 0, C: 0, F: 0 };
  data[sem].subjects.forEach((s) => {
    if (cnt[s.grade] !== undefined) cnt[s.grade]++;
  });
  return Object.values(cnt);
}

function GradeCounter({ subjects }) {
  const cnt = { O: 0, "A+": 0, A: 0, "B+": 0, B: 0, C: 0, F: 0 };
  subjects?.forEach((s) => {
    if (cnt[s.grade] !== undefined) cnt[s.grade]++;
  });
  return Object.values(cnt);
}

function SubjectCard({ code, name, cr, grade }) {
  const gradeColorClass = grade === "A+" ? "Ap" : grade === "B+" ? "Bp" : grade;
  return (
    <div className="subj-item">
      <div className="subj-code">{code.slice(-3)}</div>
      <div className="subj-name">{name}</div>
      <div className="subj-credits">{cr}cr</div>
      <div className={`subj-grade grade-${gradeColorClass}`}>{grade}</div>
    </div>
  );
}

function PerformanceZone({ subjects }) {
  const zones = { strong: [], border: [], improve: [] };
  const icons = { O: "⭐", "A+": "🌟", A: "💡", "B+": "📌", B: "⚠️", C: "🔴", F: "❌" };

  subjects?.forEach((s) => {
    const p = GRADE_PTS[s.grade];
    if (p >= 8) zones.strong.push(s);
    else if (p === 7) zones.border.push(s);
    else zones.improve.push(s);
  });

  let delay = 0;

  return (
    <div id="perfZone">
      {zones.strong.length > 0 && (
        <div className="zone-section">
          <div className="zone-label strong">✅ Strong Subjects</div>
          {zones.strong.map((s) => (
            <div key={s.code} className="zone-item strong">
              {icons[s.grade] || "📚"} {s.name}
              <span className="zone-grade-badge">{s.grade}</span>
            </div>
          ))}
        </div>
      )}
      {zones.border.length > 0 && (
        <div className="zone-section">
          <div className="zone-label borderline">⚠️ Borderline</div>
          {zones.border.map((s) => (
            <div key={s.code} className="zone-item borderline">
              📌 {s.name}
              <span className="zone-grade-badge">{s.grade}</span>
            </div>
          ))}
        </div>
      )}
      {zones.improve.length > 0 && (
        <div className="zone-section">
          <div className="zone-label improve">🔴 Needs Improvement</div>
          {zones.improve.map((s) => (
            <div key={s.code} className="zone-item improve">
              ⚠️ {s.name}
              <span className="zone-grade-badge">{s.grade}</span>
            </div>
          ))}
        </div>
      )}
      {!zones.strong.length && !zones.border.length && !zones.improve.length && <div className="zone-empty">🎉 All subjects performing excellently!</div>}
    </div>
  );
}

function RiskDetector({ risks, onSelectRisk }) {
  if (!risks || risks.length === 0) {
    return <div className="risk-empty">✅ No at-risk subjects this semester!</div>;
  }

  return (
    <div>
      {risks.map((r, i) => (
        <div
          key={i}
          className={`risk-row ${r.lvl === "danger" ? "danger-row" : "warn-row"}`}
          onClick={() => onSelectRisk(i, r.name, r.grade)}
          style={{ cursor: "pointer" }}
        >
          <span>{r.lvl === "danger" ? "🚨" : "⚠️"}</span>
          <div className={`risk-sub ${r.lvl === "warn" ? "warn-c" : ""}`}>{r.name}</div>
          <div className="risk-grades">
            <span className="rg rg-n">{GRADE_PTS[r.grade]}</span>
            <span className={`rg ${r.lvl === "danger" ? "rg-d" : "rg-w"}`}>{r.grade}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function WhatIfSimulator({ selectedSubject, currentGPA, credits }) {
  const [sliderValue, setSliderValue] = useState(4);

  if (!selectedSubject) {
    return (
      <div className="sim-select-prompt">
        <div className="prompt-icon">🎯</div>
        <div style={{ fontWeight: "600", marginBottom: "4px" }}>Select a subject to simulate</div>
        <div style={{ fontSize: "12px" }}>Click any subject in the Risk Detector above to see how improving its grade affects your GPA</div>
      </div>
    );
  }

  const grade = SIM_GRADES[parseInt(sliderValue)];
  const curPts = GRADE_PTS[selectedSubject.grade];
  const newPts = GRADE_PTS[grade];
  const ptsDiff = newPts - curPts;

  const cr = DATA[Object.keys(DATA).sort((a, b) => b - a)[0]].subjects.find((s) => s.name === selectedSubject.name)?.cr || 3;
  const totalCr = credits;
  const gpaDelta = (ptsDiff * cr) / totalCr;
  const newGPA = Math.min(10, Math.max(0, currentGPA + gpaDelta));
  const diff = newGPA - currentGPA;

  return (
    <div>
      <div className="sim-subj-display active">
        <div className="sim-pulse" style={{ background: "var(--accent)", animation: "pulse-ring 1.5s infinite" }}></div>
        <div className="sim-subj-name">{selectedSubject.name}</div>
        <div className="sim-from-badge">{selectedSubject.grade}</div>
      </div>

      <div>
        <div className="sim-grade-row">
          <span className="sim-label">Target grade:</span>
          <span className="sim-arrow">→</span>
          <span className="sim-to-badge">{grade}</span>
        </div>
        <div className="sim-grades-row">
          <span>F</span>
          <span>C</span>
          <span>B</span>
          <span>B+</span>
          <span>A</span>
          <span>A+</span>
          <span>O</span>
        </div>
        <input
          type="range"
          min="0"
          max="6"
          value={sliderValue}
          onChange={(e) => setSliderValue(e.target.value)}
          style={{ width: "100%", marginBottom: "10px" }}
        />
        <div className="sim-result-box">
          <div>
            <div className="sim-result-text">New GPA</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
              <div className="sim-result-gpa">{newGPA.toFixed(2)}</div>
              <div
                className={`sim-delta ${Math.abs(diff) < 0.001 ? "same" : diff > 0 ? "up" : "down"}`}
              >
                {Math.abs(diff) < 0.001 ? "No change" : diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2)}
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: "11px", color: "var(--text-muted)" }}>
            <div>Current</div>
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: "16px", fontWeight: "700" }}>{currentGPA.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Upload Modal ─────────────────────────────────────────────────────────────
const UPLOAD_STAGES = [
  { id: "upload", label: "Uploading to server" },
  { id: "extract", label: "Extracting subjects & grades" },
  { id: "calc", label: "Calculating GPA / CGPA" },
  { id: "save", label: "Saving results" },
];

function UploadModal({ open, onClose, onComplete }) {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | processing | done
  const [progress, setProgress] = useState(0);
  const [progLabel, setProgLabel] = useState("");
  const [stages, setStages] = useState(UPLOAD_STAGES.map(s => ({ ...s, state: "pending", detail: "" })));
  const [uploadStats, setUploadStats] = useState({ subjects: 5, credits: 24, gpa: 9.29 });
  const fileInputRef = useRef(null);

  function reset() {
    setFiles([]); setDragOver(false); setPhase("idle");
    setProgress(0); setProgLabel("");
    setStages(UPLOAD_STAGES.map(s => ({ ...s, state: "pending", detail: "" })));
    setUploadStats({ subjects: 5, credits: 24, gpa: 9.29 });
  }

  function close() { reset(); onClose(); }

  function handleFiles(fileList) {
    const newFiles = Array.from(fileList).filter(f => {
      if (!f.name.endsWith(".pdf") && f.type !== "application/pdf") { 
        alert(`"${f.name}" is not a PDF file. Skipping.`); 
        return false; 
      }
      if (f.size > 10 * 1024 * 1024) { 
        alert(`"${f.name}" is too large — max 10 MB. Skipping.`); 
        return false; 
      }
      return true;
    });
    
    // Add new files to existing files (avoid duplicates by name + size)
    const existingNames = new Set(files.map(f => `${f.name}-${f.size}`));
    const uniqueNewFiles = newFiles.filter(f => !existingNames.has(`${f.name}-${f.size}`));
    setFiles(prev => [...prev, ...uniqueNewFiles]);
  }

  function onDrop(e) {
    e.preventDefault(); setDragOver(false);
    if (phase === "idle") handleFiles(e.dataTransfer.files);
  }

  function setStageState(id, state, detail = "") {
    setStages(prev => prev.map(s => s.id === id ? { ...s, state, detail } : s));
  }

  async function animateProgress(from, to, label) {
    setProgLabel(label);
    return new Promise(res => {
      let cur = from;
      const step = () => {
        cur = Math.min(cur + 1, to);
        setProgress(cur);
        if (cur < to) requestAnimationFrame(step);
        else res();
      };
      requestAnimationFrame(step);
    });
  }

  const sleep = ms => new Promise(r => setTimeout(r, ms));

  async function startUpload() {
    if (files.length === 0) return;
    setPhase("processing");

    setStageState("upload", "active");
    await animateProgress(0, 60, "Uploading…");
    
    let responseData = null;
    try {
      const fd = new FormData();
      // Append all files to FormData
      files.forEach(file => {
        fd.append("files", file);
      });
      const ctrl = new AbortController();
      const tid = setTimeout(() => ctrl.abort(), 15000);
      const res = await fetch("https://kcg-credits-back.onrender.com/upload-marks", { method: "POST", body: fd, signal: ctrl.signal, credentials: "include" });
      clearTimeout(tid);
      
      // Handle 401 Unauthorized - user session expired
      if (res.status === 401) {
        console.warn("Session expired during upload");
        localStorage.removeItem("studentInfo");
        localStorage.removeItem("semesterData");
        window.location.href = "/";
        return;
      }
      
      if (!res.ok) throw new Error("HTTP " + res.status);
      responseData = await res.json();
    } catch (error) {
      console.error("Upload error:", error);
      setStageState("upload", "error", "Upload failed");
      await sleep(1000);
      reset();
      return;
    }
    setStageState("upload", "done", `${files.length} file(s) uploaded`);

    await animateProgress(60, 70, "Extracting…");
    setStageState("extract", "active");
    await sleep(900);
    const subjectsCount = responseData?.semester_data?.[Object.keys(responseData.semester_data)[0]]?.subjects?.length || 0;
    setStageState("extract", "done", `${subjectsCount} subjects found`);

    await animateProgress(70, 82, "Calculating…");
    setStageState("calc", "active");
    await sleep(700);
    const latestSemKey = Object.keys(responseData.semester_data)[0];
    const gpa = responseData?.semester_data?.[latestSemKey]?.gpa || 0;
    const credits = responseData?.semester_data?.[latestSemKey]?.credits || 0;
    setStageState("calc", "done", `GPA: ${gpa.toFixed(2)}`);
    
    // Update stats state
    setUploadStats({ subjects: subjectsCount, credits: credits, gpa: gpa });

    await animateProgress(82, 95, "Saving…");
    setStageState("save", "active");
    await sleep(500);
    setStageState("save", "done", "Results saved");

    await animateProgress(95, 100, "Complete!");
    await sleep(300);
    setPhase("done");
    if (onComplete) onComplete(files.map(f => f.name).join(", "), responseData);
  }

  if (!open) return null;

  return (
    <div className="modal-overlay open" onClick={e => { if (e.target === e.currentTarget) close(); }}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div className="modal-title">📄 Upload Marksheet PDF</div>
          <button className="modal-close" onClick={close}>✕</button>
        </div>

        <div className="modal-body">
          {/* Drop Zone (hidden once processing starts) */}
          {phase !== "done" && phase === "idle" && (
            <div
              className={`drop-zone ${dragOver ? "drag-over" : ""}`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <span className="drop-icon">{dragOver ? "📥" : "📂"}</span>
              <div className="drop-title">Drag & drop your PDFs here</div>
              <div className="drop-sub">or click to browse from your device</div>
              <div className="drop-divider"><span>or</span></div>
              <button className="btn btn-primary" style={{ pointerEvents: "none" }}>Browse Files</button>
              <div className="drop-formats">Supported: PDF only · Max size: 10 MB per file</div>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" style={{ display: "none" }} multiple
            onChange={e => { if (e.target.files?.length) handleFiles(e.target.files); }} />

          {/* File Preview */}
          {files.length > 0 && phase === "idle" && (
            <div className="file-preview visible">
              {files.map((file, idx) => (
                <div key={idx} className="file-meta">
                  <div className="file-icon">📋</div>
                  <div className="file-details">
                    <div className="file-name">{file.name}</div>
                    <div className="file-size">{(file.size / 1024).toFixed(1)} KB · PDF Document</div>
                  </div>
                  <button className="file-remove" onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Processing View */}
          {phase === "processing" && (
            <div style={{ marginTop: 14 }}>
              <div className="upload-progress-wrap visible">
                <div className="prog-track"><div className="progress-fill-bar" style={{ width: `${progress}%` }} /></div>
                <div className="progress-info">
                  <span>{progLabel}</span>
                  <span>{progress}%</span>
                </div>
              </div>
              <div className="upload-stages">
                {stages.map(s => (
                  <div key={s.id} className={`stage-row ${s.state}`}>
                    <div className="stage-icon">
                      {s.state === "active" && <div className="stage-spinner" />}
                      {s.state === "done" && <span className="stage-check">✓</span>}
                      {s.state === "error" && <span className="stage-err">✕</span>}
                      {s.state === "pending" && <div className="stage-dot" />}
                    </div>
                    <div className="stage-label">{s.label}</div>
                    <div className="stage-detail">{s.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Complete */}
          {phase === "done" && (
            <div className="upload-complete visible">
              <div className="complete-icon">🎉</div>
              <div className="complete-title">Upload Successful!</div>
              <div className="complete-sub">Your marksheet has been processed. Dashboard updated.</div>
              <div className="complete-stats">
                <div className="c-stat"><div className="cs-num">{uploadStats.subjects}</div><div className="cs-lbl">Subjects</div></div>
                <div className="c-stat"><div className="cs-num">{uploadStats.credits}</div><div className="cs-lbl">Credits</div></div>
                <div className="c-stat"><div className="cs-num">{uploadStats.gpa.toFixed(2)}</div><div className="cs-lbl">GPA</div></div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={close}>Cancel</button>
          {phase === "done"
            ? <button className="btn btn-primary" onClick={close}>✓ Close</button>
            : <button className="btn btn-primary" disabled={files.length === 0 || phase === "processing"}
              style={{ opacity: (files.length === 0 || phase === "processing") ? 0.5 : 1, cursor: (files.length === 0 || phase === "processing") ? "not-allowed" : "pointer" }}
              onClick={startUpload}>
              ↑ Upload & Process
            </button>
          }
        </div>
      </div>
    </div>
  );
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────
function EditModal({ open, onClose, currentSem, liveData, onSave }) {
  const [editSem, setEditSem] = useState(currentSem);
  const [rows, setRows] = useState([]);

  // Sync rows when modal opens or sem changes
  useEffect(() => {
    if (open) {
      const subjects = liveData[editSem]?.subjects || [];
      setRows(subjects.map(s => ({ ...s })));
    }
  }, [open, editSem, liveData]);

  // Reset to current sem each time the modal opens
  useEffect(() => {
    if (open) setEditSem(currentSem);
  }, [open, currentSem]);

  function updateRow(idx, field, val) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: field === "cr" ? parseInt(val) || 1 : val } : r));
  }

  function addRow() {
    setRows(prev => [...prev, { code: "", name: "", cr: 3, grade: "A" }]);
  }

  function removeRow(idx) {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx));
  }

  function save() {
    // Recalculate GPA from edited rows
    const totalCr = rows.reduce((s, r) => s + (parseInt(r.cr) || 0), 0);
    const totalPts = rows.reduce((s, r) => s + (GRADE_PTS[r.grade] || 0) * (parseInt(r.cr) || 0), 0);
    const newGPA = totalCr > 0 ? parseFloat((totalPts / totalCr).toFixed(2)) : liveData[editSem].gpa;
    onSave(editSem, rows, newGPA, totalCr);
    onClose();
  }

  if (!open) return null;

  return (
    <div className="modal-overlay open" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">✏️ Edit Semester Data</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Semester</label>
            <select className="form-select" value={editSem}
              onChange={e => setEditSem(parseInt(e.target.value))}>
              {Object.entries(liveData).map(([k, v]) => (
                <option key={k} value={k}>Semester {getOrdinal(parseInt(k))}</option>
              ))}
            </select>
          </div>

          <div className="entry-row-header">
            <span>Code</span><span>Subject Name</span><span>Cr.</span><span>Grade</span><span />
          </div>

          <div className="entry-rows-wrap">
            {rows.map((r, i) => (
              <div key={i} className="entry-row">
                <input className="form-input" value={r.code} placeholder="Code"
                  onChange={e => updateRow(i, "code", e.target.value)} />
                <input className="form-input" value={r.name} placeholder="Subject name"
                  onChange={e => updateRow(i, "name", e.target.value)} />
                <input className="form-input" type="number" value={r.cr} min="1" max="6"
                  onChange={e => updateRow(i, "cr", e.target.value)} />
                <select className="form-select" value={r.grade}
                  onChange={e => updateRow(i, "grade", e.target.value)}>
                  {GRADES_LIST.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
                <button className="btn-icon btn-rem" onClick={() => removeRow(i)}
                  title="Remove row">−</button>
              </div>
            ))}
          </div>

          <button className="add-row-btn" onClick={addRow}>+ Add Subject</button>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save}>💾 Save Changes</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { dark, toggle } = useTheme();
  
  // Load student data from localStorage
  const getInitialStudentInfo = () => {
    try {
      const stored = localStorage.getItem("studentInfo");
      if (stored) {
        const info = JSON.parse(stored);
        return {
          student_name: info.student_name || "Student",
          reg_no: info.reg_no || "N/A",
          program: info.program || "B.E – CS"
        };
      }
    } catch (e) {
      console.error("Error parsing student info:", e);
    }
    return {
      student_name: "John Doe",
      reg_no: "20KCCE001",
      program: "B.E – CS"
    };
  };

  const getInitialLiveData = () => {
    try {
      const stored = localStorage.getItem("semesterData");
      if (stored) {
        const parsed = JSON.parse(stored);
        // If data is empty or invalid, return empty object (don't show demo data)
        if (parsed && Object.keys(parsed).length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.error("Error parsing semester data:", e);
    }
    return {}; // Return empty object instead of demo DATA
  };

  const [currentSem, setCurrentSem] = useState(() => {
    const data = getInitialLiveData();
    const semesters = Object.keys(data).map(Number);
    return semesters.length > 0 ? Math.max(...semesters) : 1;
  });
  const [activeSection, setActiveSection] = useState("dashboard");
  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  const [selectedRiskSubj, setSelectedRiskSubj] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastShow, setToastShow] = useState(false);
  const [liveData, setLiveData] = useState(() => getInitialLiveData());
  const [uploadStatus, setUploadStatus] = useState("📄 marksheet · ✓ Completed");
  const [trendTabActive, setTrendTabActive] = useState("both");
  const [contributionTabActive, setContributionTabActive] = useState("gpa");
  const [studentInfo, setStudentInfo] = useState(() => getInitialStudentInfo());
  const [leaderboardType, setLeaderboardType] = useState("class");
  const [classLeaderboardView, setClassLeaderboardView] = useState(5);
  const toastTimerRef = useRef(null);
  const gpaChartRef = useRef(null);
  const gradeChartRef = useRef(null);
  const gpaChartInstanceRef = useRef(null);
  const gradeChartInstanceRef = useRef(null);

  const currentData = liveData[currentSem];

  // Calculate all GPAs from liveData and CGPA
  const allGPAs = Object.keys(liveData)
    .sort((a, b) => parseInt(a) - parseInt(b))
    .map(sem => liveData[sem].gpa);

  const calculateCGPA = (gpas) => {
    if (gpas.length === 0) return 0;
    const sum = gpas.reduce((acc, gpa) => acc + gpa, 0);
    return parseFloat((sum / gpas.length).toFixed(2));
  };

  // Calculate cumulative CGPA at each semester
  const allCGPAs = [];
  if (allGPAs.length > 0) {
    for (let i = 0; i < allGPAs.length; i++) {
      const cgpa = calculateCGPA(allGPAs.slice(0, i + 1));
      allCGPAs.push(cgpa);
    }
  }

  // Fetch fresh user data from /me endpoint on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch("https://kcg-credits-back.onrender.com/me", {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        // Handle 401 Unauthorized - user session expired
        if (response.status === 401) {
          console.warn("Session expired or unauthorized");
          localStorage.removeItem("studentInfo");
          localStorage.removeItem("semesterData");
          window.location.href = "/";
          return;
        }

        if (!response.ok) {
          console.error("Failed to fetch user data:", response.status);
          return;
        }

        const data = await response.json();

        // Update localStorage with fresh data
        if (data.student_info) {
          localStorage.setItem("studentInfo", JSON.stringify(data.student_info));
          setStudentInfo({
            student_name: data.student_info.student_name || "Student",
            reg_no: data.student_info.reg_no || "N/A",
            program: data.student_info.program || "B.E – CS"
          });
        }

        if (data.semester_data && Object.keys(data.semester_data).length > 0) {
          localStorage.setItem("semesterData", JSON.stringify(data.semester_data));
          setLiveData(data.semester_data);
          // Update current semester to the latest one
          const semesters = Object.keys(data.semester_data).map(Number);
          if (semesters.length > 0) {
            setCurrentSem(Math.max(...semesters));
          }
        } else {
          // Clear previous semester data if new response has none
          localStorage.removeItem("semesterData");
          setLiveData({});
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []); // Run only on mount

  // Chart initialization
  useEffect(() => {
    const initCharts = () => {
      const dk = dark;
      const gridColor = dk ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.06)";
      const textColor = dk ? "#94a3b8" : "#64748b";

      // Destroy existing charts
      if (gpaChartInstanceRef.current) gpaChartInstanceRef.current.destroy();
      if (gradeChartInstanceRef.current) gradeChartInstanceRef.current.destroy();

      // GPA Chart
      if (gpaChartRef.current) {
        // Filter datasets based on selected tab
        let datasets = [];
        if (trendTabActive === "both" || trendTabActive === "gpa") {
          datasets.push({
            label: "GPA",
            data: allGPAs,
            borderColor: "#2563eb",
            backgroundColor: "rgba(37,99,235,.1)",
            tension: 0.4,
            fill: true,
            pointBackgroundColor: "#2563eb",
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2.5,
          });
        }
        if (trendTabActive === "both" || trendTabActive === "cgpa") {
          datasets.push({
            label: "CGPA",
            data: allCGPAs,
            borderColor: "#16a34a",
            backgroundColor: "rgba(22,163,74,.07)",
            tension: 0.4,
            fill: true,
            pointBackgroundColor: "#16a34a",
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2,
            borderDash: [4, 3],
          });
        }

        gpaChartInstanceRef.current = new Chart(gpaChartRef.current, {
          type: "line",
          data: {
            labels: Object.keys(liveData)
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map(sem => `Sem ${getOrdinal(parseInt(sem))}`),
            datasets: datasets,
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: { mode: "index", intersect: false },
            },
            scales: {
              x: {
                grid: { color: gridColor },
                ticks: { color: textColor, font: { family: "Outfit", size: 11 } },
              },
              y: {
                min: 5,
                max: 10,
                grid: { color: gridColor },
                ticks: { color: textColor, font: { family: "Outfit", size: 11 } },
              },
            },
            animation: { duration: 800, easing: "easeInOutQuart" },
          },
        });
      }

      // Grade Chart
      if (gradeChartRef.current) {
        gradeChartInstanceRef.current = new Chart(gradeChartRef.current, {
          type: "bar",
          data: {
            labels: ["O", "A+", "A", "B+", "B", "C", "F"],
            datasets: [
              {
                data: gradeDist(currentSem, liveData),
                backgroundColor: [
                  "#16a34a",
                  "#22c55e",
                  "#4ade80",
                  "#f59e0b",
                  "#fb923c",
                  "#ef4444",
                  "#dc2626",
                ],
                borderRadius: 6,
                borderSkipped: false,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) => ` ${ctx.parsed.y} subject(s)`,
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: textColor, font: { family: "Outfit", size: 11 } },
              },
              y: {
                beginAtZero: true,
                max: 5,
                grid: { color: gridColor },
                ticks: {
                  color: textColor,
                  stepSize: 1,
                  font: { family: "Outfit", size: 11 },
                },
              },
            },
            animation: { duration: 700, easing: "easeOutBounce" },
          },
        });
      }
    };

    initCharts();

    return () => {
      if (gpaChartInstanceRef.current) gpaChartInstanceRef.current.destroy();
      if (gradeChartInstanceRef.current) gradeChartInstanceRef.current.destroy();
    };
  }, [dark, currentSem, activeSection, trendTabActive, liveData]);

  const showToast = useCallback((msg, color = "#16a34a") => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMsg(msg);
    setToastShow(true);
    toastTimerRef.current = setTimeout(() => setToastShow(false), 3200);
  }, []);

  const handleSelectRiskSubject = (idx, name, grade) => {
    setSelectedRiskSubj({ name, grade, curGPA: currentData.gpa });
    showToast(`Simulating: ${name} 🔮`, "#2563eb");
  };

  const handleSemesterChange = (newSem) => {
    setCurrentSem(parseInt(newSem));
  };

  const handleLogout = async () => {
    try {
      const response = await fetch("https://kcg-credits-back.onrender.com/logout", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        // Clear localStorage
        localStorage.removeItem("studentInfo");
        localStorage.removeItem("semesterData");
        // Redirect to login
        showToast("Logged out successfully! 👋", "#22c55e");
        setTimeout(() => window.location.href = "/", 1500);
      } else {
        showToast("Logout failed. Try again.", "#ef4444");
      }
    } catch (error) {
      console.error("Logout error:", error);
      showToast("Error during logout.", "#ef4444");
    }
  };

  const handlePrintDashboard = () => {
    window.print();
    showToast("Preparing dashboard for print... 🖨️", "#16a34a");
  };

  const getLeaderboardData = () => {
    return LB_DATA.map((s, i) => ({ ...s, rank: i + 1 }));
  };

  const handleSaveEdit = useCallback((sem, rows, newGPA, newCredits) => {
    setLiveData(prev => ({
      ...prev,
      [sem]: {
        ...prev[sem],
        subjects: rows,
        gpa: newGPA,
        credits: newCredits,
        risk: rows.filter(r => GRADE_PTS[r.grade] <= 6).map(r => ({
          name: r.name, grade: r.grade,
          lvl: GRADE_PTS[r.grade] <= 5 ? "danger" : "warn",
        })),
      },
    }));
    showToast(`Semester ${sem} data saved ✅`, "#16a34a");
  }, [showToast]);

  const handleUploadComplete = useCallback((fileName, responseData) => {
    if (responseData) {
      // Update student info
      if (responseData.student_info) {
        setStudentInfo(responseData.student_info);
      }

      // Process semester data - transform it to match our format
      if (responseData.semester_data) {
        const processedData = {};
        Object.entries(responseData.semester_data).forEach(([semKey, semData]) => {
          const semNumber = parseInt(semKey);
          // Transform response data to match our DATA format
          const transformedSem = {
            label: semData.label,
            credits: semData.credits,
            gpa: semData.gpa,
            subjects: semData.subjects.map(s => ({
              code: s.code,
              name: s.name,
              cr: s.cr,
              grade: s.grade
            })),
            contrib: semData.contrib || [], // Keep original contrib if available
            risk: semData.risk || []
          };
          processedData[semNumber] = transformedSem;
        });

        // Update liveData with processed data
        setLiveData(prev => ({
          ...prev,
          ...processedData
        }));

        // Set current semester to the latest uploaded semester
        const latestSem = Math.max(...Object.keys(processedData).map(Number));
        setCurrentSem(latestSem);
      }
    }

    setUploadStatus(`📄 ${fileName} · ✓ Completed`);
    showToast("Marksheet processed successfully! 🎉", "#16a34a");
  }, [showToast]);

  return (
    <div className="dashboard-page" data-theme={dark ? "dark" : "light"}>
      <style>{dashboardCSS}</style>

      {/* Background effects */}
      <Stars />
      <Particles />
      <Clouds />

      {/* HEADER */}
      <header>
        {/* Logo */}
        <div className="logo-area">
          <div className="logo-arc" />
          <div className="logo-txt">KCGCredits<sup>®</sup></div>
          <div className="tagline">Student Portal</div>
        </div>
        <nav className={`header-nav ${hamburgerOpen ? "mobile-open" : ""}`} id="headerNav">
          <button
            className={`nav-link ${activeSection === "dashboard" ? "active" : ""}`}
            onClick={() => {
              setActiveSection("dashboard");
              setHamburgerOpen(false);
            }}
          >
            📊 Dashboard
          </button>
          <button
            className={`nav-link ${activeSection === "leaderboard" ? "active" : ""}`}
            onClick={() => {
              setActiveSection("leaderboard");
              setHamburgerOpen(false);
            }}
          >
            🏆 Leaderboard
          </button>
          <button
            className={`nav-link ${activeSection === "about" ? "active" : ""}`}
            onClick={() => {
              setActiveSection("about");
              setHamburgerOpen(false);
            }}
          >
            👨‍💻 About Dev
          </button>
        </nav>
        <button
          className={`hamburger-btn ${hamburgerOpen ? "active" : ""}`}
          onClick={() => setHamburgerOpen(!hamburgerOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className="header-right">
          <div className="welcome">👤 Welcome, {studentInfo.student_name}</div>
          <button className="btn btn-outline" onClick={handlePrintDashboard} title="Export PDF">
            ↑ <span className="btn-text">Export PDF</span>
          </button>
          <button className="btn btn-outline" onClick={() => showToast("Share link copied! 🔗", "#2563eb")} title="Share">
            ↗ <span className="btn-text">Share</span>
          </button>
          <button
            className="theme-toggle"
            onClick={toggle}
            aria-label="Toggle theme"
            title={dark ? "Light mode" : "Dark mode"}
          >
            {dark ? "🌙" : "☀️"}
          </button>
          <button
            className="btn btn-logout"
            onClick={handleLogout}
            title="Logout"
            aria-label="Logout"
          >
          <Logout/>
          <span className="btn-text">Logout</span>
          </button>
        </div>
      </header>

      {/* MAIN CONTENT */}
      {activeSection === "dashboard" && (
        <main>
          {/* Upload Card */}
          <div className="upload-card">
            <div className="upload-inner">
              <div className="scene">
                {dark ? <Logon /> : <Logo />}
              </div>
              <div className="upload-info">
                <h2>Upload your official semester marksheet PDF.</h2>
                <p>We automatically extract subjects, credits, grades, and calculate GPA 🤖 📊</p>
                <div className="upload-actions">
                  <button className="btn btn-primary" onClick={() => setUploadOpen(true)}>
                    ↑ Upload PDF
                  </button>
                  <div className="upload-status" id="uploadStatusBadge">
                    {uploadStatus}
                  </div>
                </div>
                <div className="progress-steps">
                  <div className="step done">✓ Subject Extraction</div>
                  <div className="step done">✓ Grade Calculation</div>
                  <div className="step done">✓ GPA Calculation</div>
                  <div className="step done">✓ Slip 2 Updated</div>
                  <button className="edit-btn" onClick={() => setEditOpen(true)}>
                    ✏ Edit Semester Data
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Empty State - No Semester Data Yet */}
          {Object.keys(liveData).length === 0 && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 20px",
              textAlign: "center",
              gap: "20px"
            }}>
              <div style={{ fontSize: "64px" }}>📋</div>
              <h2 style={{ color: "var(--text-primary)", margin: "10px 0" }}>No Semester Data Available</h2>
              <p style={{ color: "var(--text-muted)", maxWidth: "500px", lineHeight: "1.6" }}>
                Upload your first semester marksheet PDF to get started. We'll automatically extract your subjects, grades, and calculate your GPA.
              </p>
              <button className="btn btn-primary" onClick={() => setUploadOpen(true)} style={{ marginTop: "10px" }}>
                ↑ Upload Your First Marksheet
              </button>
            </div>
          )}

          {/* Grid Row 1 */}
          {Object.keys(liveData).length > 0 && (
            <>
            <div className="grid-main">
            {/* Student Card */}
            <div className="card">
              <div className="card-title">Student Summary Card</div>
              <div className="student-avatar">🎓</div>
              <div className="student-name">{studentInfo.student_name}</div>
              <div className="student-info">
                <div className="info-row">
                  🎫 Register: <strong>{studentInfo.reg_no}</strong>
                </div>
                <div className="info-row">
                  🎓 Program: <strong>{studentInfo.program}</strong>
                </div>
                <div className="info-row">
                  📅 Semester: <strong>{getOrdinal(currentSem)}</strong>
                </div>
              </div>
              <div className="credits-row">
                <div className="credit-badge">
                  <div className="num">{currentData.credits}</div>
                  <div className="lbl">Registered</div>
                </div>
                <div className="credit-badge">
                  <div className="num">{currentData.credits}</div>
                  <div className="lbl">Completed</div>
                </div>
                <div className="credit-badge">
                  <div className="num">{currentData.gpa.toFixed(2)}</div>
                  <div className="lbl">GPA</div>
                </div>
                <div className="credit-badge">
                  <div className="num">{allCGPAs.length > 0 ? allCGPAs[allCGPAs.length - 1].toFixed(2) : "0.00"}</div>
                  <div className="lbl">CGPA</div>
                </div>
              </div>
            </div>

            {/* GPA Trend Card */}
            <div className="card">
              <div className="card-title">
                GPA vs CGPA Trend
                <div className="tab-group">
                  <button className={`tab ${trendTabActive === "both" ? "active" : ""}`} onClick={() => setTrendTabActive("both")}>Both</button>
                  <button className={`tab ${trendTabActive === "gpa" ? "active" : ""}`} onClick={() => setTrendTabActive("gpa")}>GPA</button>
                  <button className={`tab ${trendTabActive === "cgpa" ? "active" : ""}`} onClick={() => setTrendTabActive("cgpa")}>CGPA</button>
                </div>
              </div>
              <div className="chart-container" style={{ position: "relative", height: "190px" }}>
                <canvas ref={gpaChartRef}></canvas>
              </div>
              <div className="chart-legend">
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: "#2563eb" }}></div>GPA
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: "#16a34a" }}></div>CGPA
                </div>
              </div>
              <div className="chart-badge">✅ Consistent upward trend — academic improvement ↗</div>
            </div>

            {/* Subject List Card */}
            <div className="card">
              <div className="card-title">
                Subjects
                <div className="sem-dropdown-wrap">
                  <span className="sem-label">Semester:</span>
                  <select className="sem-dropdown" value={currentSem} onChange={(e) => handleSemesterChange(e.target.value)}>
                    {Object.entries(DATA).map(([sem, data]) => (
                      <option key={sem} value={sem}>
                        Sem {getOrdinal(parseInt(sem))}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="subject-list-panel">
                {currentData.subjects.map((s) => (
                  <SubjectCard key={s.code} {...s} />
                ))}
              </div>
            </div>
          </div>

          {/* Grid Row 2 */}
          <div className="grid-3">
            {/* Contribution Card */}
            <div className="card">
              <div className="card-title">
                Subject Contribution
                <div className="tab-group">
                  <button className={`tab ${contributionTabActive === "gpa" ? "active" : ""}`} onClick={() => setContributionTabActive("gpa")}>GPA</button>
                  <button className={`tab ${contributionTabActive === "risk" ? "active" : ""}`} onClick={() => setContributionTabActive("risk")}>Risk</button>
                </div>
              </div>
              <div className="subject-bars">
                {(contributionTabActive === "gpa" 
                  ? currentData.subjects 
                  : currentData.subjects.filter(s => currentData.risk.some(r => r.name === s.name))
                ).map((s, idx) => {
                  const i = currentData.subjects.indexOf(s);
                  const c = currentData.contrib[i];
                  const w = Math.min(98, Math.round((c.val / 5) * 100) + 10);
                  return (
                    <div key={s.code} className="subject-bar-row">
                      <div className="subject-code-lbl">{s.code.slice(-3)}</div>
                      <div className="bar-track">
                        <div className="bar-fill" style={{ width: `${w}%` }}>
                          <div className="bar-seg seg-green" style={{ width: `${c.g}%` }}></div>
                          <div className="bar-seg seg-orange" style={{ width: `${c.o}%` }}></div>
                          <div className="bar-seg seg-red" style={{ width: `${c.r}%` }}></div>
                        </div>
                      </div>
                      <div className="bar-val">{c.val.toFixed(1)}</div>
                    </div>
                  );
                })}
                {contributionTabActive === "risk" && currentData.risk.length === 0 && (
                  <div style={{ textAlign: "center", padding: "20px", color: "var(--primary)", fontSize: "13px", fontWeight: "600" }}>
                    ✅ No risk subjects in this semester
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: "14px", marginTop: "14px" }}>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: "#22c55e" }}></div>High
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: "#f59e0b" }}></div>Med
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: "#ef4444" }}></div>Low
                </div>
              </div>
            </div>

            {/* Performance Zone Card */}
            <div className="card">
              <div className="card-title">Performance Zone Analysis</div>
              <PerformanceZone subjects={currentData.subjects} />
            </div>

            {/* Risk & Simulator Card */}
            <div className="card">
              <div className="card-title">Risk & Improvement Insights</div>

              <div className="risk-inner">
                <div className="risk-title">
                  🎯 Academic Risk Detector
                  <span className="risk-hint">
                    <span className="risk-hint-icon">👆</span> Click to simulate
                  </span>
                </div>
                <RiskDetector risks={currentData.risk} onSelectRisk={handleSelectRiskSubject} />
              </div>

              <div className="risk-inner">
                <div className="risk-title">🔮 What-if Simulator</div>
                <WhatIfSimulator selectedSubject={selectedRiskSubj} currentGPA={currentData.gpa} credits={currentData.credits} />
              </div>
            </div>
          </div>

          {/* Grid Row 3 */}
          <div className="grid-2">
            <div className="card">
              <div className="card-title">
                Grade Distribution <span className="card-badge">Sem {getOrdinal(currentSem)}</span>
              </div>
              <div className="grade-chart-container" style={{ position: "relative", height: "130px" }}>
                <canvas ref={gradeChartRef}></canvas>
              </div>
            </div>
            <div className="card">
              <div className="card-title">
                Semester Comparison <span className="card-badge">{allGPAs.length} {allGPAs.length === 1 ? "semester" : "semesters"}</span>
              </div>
              <div className="stats-row">
                <div className="stat-box">
                  <div className="stat-num">{currentData.gpa.toFixed(2)}</div>
                  <div className="stat-lbl">Current</div>
                </div>
                <div className="stat-box">
                  <div className="stat-num">{allGPAs.length > 0 ? (allGPAs.reduce((a, b) => a + b) / allGPAs.length).toFixed(2) : "0.00"}</div>
                  <div className="stat-lbl">Average</div>
                </div>
                <div className="stat-box">
                  <div className="stat-num">{allGPAs.length > 0 ? Math.min(...allGPAs).toFixed(2) : "0.00"}</div>
                  <div className="stat-lbl">Lowest</div>
                </div>
              </div>
              <div className="sem-chips">
                {allGPAs.map((v, i) => {
                  const semNum = Object.keys(liveData).sort((a, b) => parseInt(a) - parseInt(b))[i];
                  return (
                    <div
                      key={i}
                      className={`sem-chip ${parseInt(semNum) === currentSem ? "active" : ""} ${v === Math.min(...allGPAs) ? "low" : ""}`}
                      onClick={() => handleSemesterChange(parseInt(semNum))}
                    >
                      <div className="chip-lbl">Sem {getOrdinal(parseInt(semNum))}</div>
                      <div className="chip-val">{v.toFixed(2)}</div>
                    </div>
                  );
                })}
              </div>
              <div className="prog-wrap">
                <div className="prog-track">
                  <div className="prog-fill" style={{ width: `${(currentData.gpa / 10) * 100}%` }}></div>
                </div>
                <div className="prog-labels">
                  <span>0.0</span>
                  <span className="trend-up">↑ Improving</span>
                  <span>10.0</span>
                </div>
              </div>
            </div>
          </div>
          </>
          )}
        </main>
      )}

      {/* LEADERBOARD SECTION */}
      {activeSection === "leaderboard" && (
        <main>
          {/* Leaderboard Type Tabs */}
          <div className="lb-type-tabs">
            <button 
              className={`lb-type-tab ${leaderboardType === "class" ? "active" : ""}`}
              onClick={() => setLeaderboardType("class")}
            >
              <span className="tab-icon">👥</span>
              <span className="tab-label">Class Leaderboard</span>
            </button>
            <button 
              className={`lb-type-tab ${leaderboardType === "department" ? "active" : ""}`}
              onClick={() => setLeaderboardType("department")}
            >
              <span className="tab-icon">🏢</span>
              <span className="tab-label">Department Leaderboard</span>
            </button>
            <button 
              className={`lb-type-tab ${leaderboardType === "college" ? "active" : ""}`}
              onClick={() => setLeaderboardType("college")}
            >
              <span className="tab-icon">🎓</span>
              <span className="tab-label">College Leaderboard</span>
            </button>
          </div>

          {/* CLASS LEADERBOARD */}
          {leaderboardType === "class" && (
            <>
              <div className="lb-hero class-hero">
                <div className="lb-hero-inner">
                  <div className="lb-trophy">🏆</div>
                  <div>
                    <h1 className="lb-title">Class Leaderboard</h1>
                    <p className="lb-sub">
                      {(() => {
                        const currentUser = LB_DATA.find(s => s.me);
                        const viewLabel = classLeaderboardView === "cgpa" ? "Overall CGPA" : `Semester ${classLeaderboardView}`;
                        return `${studentInfo.program} · Class ${currentUser?.studentClass} · ${viewLabel}`;
                      })()}
                    </p>
                  </div>
                  <div className="lb-filters">
                    <select className="sem-dropdown" value={classLeaderboardView} onChange={(e) => setClassLeaderboardView(e.target.value === "cgpa" ? "cgpa" : parseInt(e.target.value))}>
                      <option value="5">Sem 5</option>
                      <option value="4">Sem 4</option>
                      <option value="3">Sem 3</option>
                      <option value="2">Sem 2</option>
                      <option value="1">Sem 1</option>
                      <option value="cgpa">CGPA (Overall)</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="podium-row">
                {(() => {
                  const sortedLB = [...LB_DATA].sort((a, b) => {
                    const aVal = classLeaderboardView === "cgpa" ? a.cgpa : a.gpa[classLeaderboardView];
                    const bVal = classLeaderboardView === "cgpa" ? b.cgpa : b.gpa[classLeaderboardView];
                    return bVal - aVal;
                  });
                  return sortedLB.slice(0, 3).map((student, idx) => {
                    const order = [1, 0, 2];
                    const actualIdx = order[idx];
                    const s = sortedLB[actualIdx];
                    const val = classLeaderboardView === "cgpa" ? s.cgpa : s.gpa[classLeaderboardView];
                    return (
                      <div key={s.reg} className={`podium-card rank-${idx + 1}`}>
                        <div className="podium-medal">{MEDALS[actualIdx]}</div>
                        <div className="podium-rank">#{actualIdx + 1} Place</div>
                        <div className="podium-name">{s.name}</div>
                        <div className="podium-reg">{s.reg}</div>
                        <div className="podium-gpa">{val.toFixed(2)}</div>
                        <div className="podium-lbl">{classLeaderboardView === "cgpa" ? "CGPA" : "GPA"}</div>
                      </div>
                    );
                  });
                })()}
              </div>

              <div className="card lb-table-card">
                <div className="card-title">
                  All Students <span className="card-badge">{LB_DATA.length} students</span>
                </div>
                <div className="lb-search-wrap">
                  <input className="lb-search" type="text" placeholder="🔍 Search by name or roll number…" />
                </div>
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Student</th>
                      <th>{classLeaderboardView === "cgpa" ? "CGPA" : "GPA"}</th>
                      <th>Performance</th>
                      <th>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const sortedLB = [...LB_DATA].sort((a, b) => {
                        const aVal = classLeaderboardView === "cgpa" ? a.cgpa : a.gpa[classLeaderboardView];
                        const bVal = classLeaderboardView === "cgpa" ? b.cgpa : b.gpa[classLeaderboardView];
                        return bVal - aVal;
                      });
                      return sortedLB.map((s, i) => {
                        const val = classLeaderboardView === "cgpa" ? s.cgpa : s.gpa[classLeaderboardView];
                        return (
                          <tr key={s.reg} className={`lb-table-row ${s.me ? "lb-me-row" : ""} lb-row-${i}`}>
                            <td className="lb-rank-cell">{i + 1 <= 3 ? MEDALS[i] : `#${i + 1}`}</td>
                            <td>
                              <strong>{s.name}</strong>
                              {s.me && <span className="lb-me-badge">You</span>}
                              <br />
                              <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{s.reg}</span>
                            </td>
                            <td className="lb-gpa-cell" style={{ color: "var(--primary)" }}>
                              {val.toFixed(2)}
                            </td>
                            <td>
                              <div className="lb-bar-wrap">
                                <div className="lb-bar-fill" style={{ width: `${(val / 10) * 100}%` }}></div>
                              </div>
                            </td>
                            <td className="lb-trend">↑</td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>

              <div className="card my-rank-card">
                <div className="my-rank-inner">
                  {(() => {
                    // Find current user in LB_DATA
                    const currentUser = LB_DATA.find(s => s.me);
                    const sortedLB = [...LB_DATA].sort((a, b) => {
                      const aVal = classLeaderboardView === "cgpa" ? a.cgpa : a.gpa[classLeaderboardView];
                      const bVal = classLeaderboardView === "cgpa" ? b.cgpa : b.gpa[classLeaderboardView];
                      return bVal - aVal;
                    });
                    const myRank = sortedLB.findIndex(s => s.me) + 1;
                    const myVal = classLeaderboardView === "cgpa" ? currentUser.cgpa : currentUser.gpa[classLeaderboardView];
                    const percentile = ((LB_DATA.length - myRank + 1) / LB_DATA.length * 100).toFixed(0);
                    
                    return (
                      <>
                        <div className="my-rank-left">
                          <div className="my-rank-num">#{myRank}</div>
                          <div>
                            <div className="my-rank-label">Your Rank in Class</div>
                            <div className="my-rank-sub">{studentInfo.student_name} · {studentInfo.reg_no}</div>
                          </div>
                        </div>
                        <div className="my-rank-right">
                          <div className="my-rank-stat">
                            <span>{myVal.toFixed(2)}</span>
                            <span>{classLeaderboardView === "cgpa" ? "CGPA" : "GPA"}</span>
                          </div>
                          <div className="my-rank-stat">
                            <span>Top {percentile}%</span>
                            <span>Percentile</span>
                          </div>
                          <div className="my-rank-stat">
                            <span>↑</span>
                            <span>Trend</span>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </>
          )}

          {/* DEPARTMENT LEADERBOARD */}
          {leaderboardType === "department" && (
            <>
              <div className="lb-hero dept-hero">
                <div className="lb-hero-inner">
                  <div className="lb-trophy">🏢</div>
                  <div>
                    <h1 className="lb-title">Department Leaderboard</h1>
                    <p className="lb-sub">
                      {(() => {
                        const currentUser = LB_DATA.find(s => s.me);
                        return `Top Students by CGPA · ${currentUser?.department} department`;
                      })()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="dept-podium-row">
                {DEPT_LB_DATA.slice(0, 3).map((student, idx) => {
                  const order = [1, 0, 2];
                  const actualIdx = order[idx];
                  const s = DEPT_LB_DATA[actualIdx];
                  return (
                    <div key={actualIdx} className={`dept-podium-card rank-${idx + 1}`}>
                      <div className="podium-medal">{MEDALS[actualIdx]}</div>
                      <div className="podium-rank">#{actualIdx + 1} Place</div>
                      <div className="dept-name">{s.name}</div>
                      <div className="dept-abbr">Class {s.studentClass} · {s.department}</div>
                      <div className="dept-gpa">{s.cgpa.toFixed(2)}</div>
                      <div className="dept-lbl">CGPA</div>
                      <div className="dept-sub-info">{s.reg}</div>
                    </div>
                  );
                })}
              </div>

              <div className="card dept-table-card">
                <div className="card-title">
                  All Students <span className="card-badge">{DEPT_LB_DATA.length} students</span>
                </div>
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Student Name</th>
                      <th>Class</th>
                      <th>CGPA</th>
                      <th>Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {DEPT_LB_DATA.map((s, i) => (
                      <tr key={i} className={`lb-table-row dept-row-${i}`}>
                        <td className="lb-rank-cell">{i + 1 <= 3 ? MEDALS[i] : `#${i + 1}`}</td>
                        <td>
                          <strong>{s.name}</strong>
                          <br />
                          <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{s.reg}</span>
                        </td>
                        <td style={{ fontWeight: "600" }}>{s.studentClass}</td>
                        <td className="lb-gpa-cell" style={{ color: "var(--primary)", fontWeight: "700" }}>
                          {s.cgpa.toFixed(2)}
                        </td>
                        <td>
                          <div className="lb-bar-wrap">
                            <div className="lb-bar-fill" style={{ width: `${(s.cgpa / 10) * 100}%` }}></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* COLLEGE LEADERBOARD */}
          {leaderboardType === "college" && (
            <>
              <div className="lb-hero college-hero">
                <div className="lb-hero-inner">
                  <div className="lb-trophy">🎓</div>
                  <div>
                    <h1 className="lb-title">College Leaderboard</h1>
                    <p className="lb-sub">Top Students by CGPA · Academic Excellence · KCG College All Departments</p>
                  </div>
                </div>
              </div>

              <div className="college-podium-row">
                {COLLEGE_LB_DATA.slice(0, 3).map((student, idx) => {
                  const order = [1, 0, 2];
                  const actualIdx = order[idx];
                  const s = COLLEGE_LB_DATA[actualIdx];
                  return (
                    <div key={actualIdx} className={`college-podium-card rank-${idx + 1}`}>
                      <div className="podium-medal">{MEDALS[actualIdx]}</div>
                      <div className="podium-rank">#{actualIdx + 1} Place</div>
                      <div className="college-name">{s.name}</div>
                      <div className="college-abbr">{s.department}</div>
                      <div className="college-gpa">{s.cgpa.toFixed(2)}</div>
                      <div className="college-lbl">CGPA</div>
                      <div className="college-sub-info">{s.reg}</div>
                    </div>
                  );
                })}
              </div>

              <div className="card college-table-card">
                <div className="card-title">
                  All Students <span className="card-badge">{COLLEGE_LB_DATA.length} students</span>
                </div>
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Student Name</th>
                      <th>Department</th>
                      <th>CGPA</th>
                      <th>Performance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COLLEGE_LB_DATA.map((s, i) => (
                      <tr key={i} className={`lb-table-row college-row-${i}`}>
                        <td className="lb-rank-cell">{i + 1 <= 3 ? MEDALS[i] : `#${i + 1}`}</td>
                        <td>
                          <strong>{s.name}</strong>
                          <br />
                          <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{s.reg}</span>
                        </td>
                        <td style={{ fontWeight: "600" }}>{s.department}</td>
                        <td className="lb-gpa-cell" style={{ color: "var(--primary)", fontWeight: "700" }}>
                          {s.cgpa.toFixed(2)}
                        </td>
                        <td>
                          <div className="lb-bar-wrap">
                            <div className="lb-bar-fill" style={{ width: `${(s.cgpa / 10) * 100}%` }}></div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </main>
      )}

      {/* ABOUT SECTION */}
      {activeSection === "about" && (
        <main className="about-main">
          <div className="about-hero">
            <div className="about-avatar-wrap">
              <div className="about-avatar">💻</div>
              <div className="about-status-dot"></div>
            </div>
            <div className="about-info">
              <h1 className="about-name">Sachin M</h1>
              <div className="about-title-badge">Full-Stack Developer · KCG College of Technology</div>
              <p className="about-bio">
                Passionate <strong>MCA student</strong> and developer who built <strong>KCG Credits</strong> to simplify academic tracking for peers. I love turning complex problems into clean, intuitive interfaces. Currently exploring AI/ML and distributed systems.
              </p>
              <div className="about-links">
                <a className="about-link github" href="https://github.com/sachin-m-on-git" onClick={(e) => { e.preventDefault(); showToast("Opening GitHub… 🚀", "#1a1a2e"); }}>
                  <Github/>
                  GitHub
                </a>
                <a className="about-link linkedin" href="#" onClick={(e) => { e.preventDefault(); showToast("Opening LinkedIn… 💼", "#0077b5"); }}>
                  <Linkedin/>
                  LinkedIn
                </a>
                <a className="about-link email" href="#" onClick={(e) => { e.preventDefault(); showToast("Opening mail… 📧", "#ea4335"); }}>
                  ✉ Email
                </a>
                <a className="about-link portfolio" href="#" onClick={(e) => { e.preventDefault(); showToast("Portfolio coming soon! 🌐", "#7c3aed"); }}>
                  🌐 Portfolio
                </a>
              </div>
            </div>
          </div>

          <div className="about-grid">
            <div className="card">
              <div className="card-title">⚙️ Tech Stack</div>
              <div className="tech-grid">
                <div className="tech-item">
                  <span className="tech-icon">🌐</span>
                  <span>HTML / CSS / JS</span>
                </div>
                <div className="tech-item">
                  <Reacti/>
                  <span>React</span>
                </div>
                <div className="tech-item">
                  <Nodejs/>
                  <span>Node.js</span>
                </div>
                <div className="tech-item">
                  <Python/>
                  <span>Python</span>
                </div>
                <div className="tech-item">
                  <Mongodb/>
                  <span>Mongo DB</span>
                </div>
                <div className="tech-item">
                  <Docker/>
                  <span>Docker</span>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-title">📈 Project Stats</div>
              <div className="about-stats-grid">
                <div className="about-stat">
                  <div className="about-stat-num" style={{ color: "var(--primary)" }}>
                    2.4k
                  </div>
                  <div className="about-stat-lbl">Lines of Code</div>
                </div>
                <div className="about-stat">
                  <div className="about-stat-num" style={{ color: "var(--accent)" }}>6</div>
                  <div className="about-stat-lbl">Weeks to Build</div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div class="card-title">📖 The Story Behind KCG Credits</div>
            <div class="about-story">
              <div class="story-block">
                <div class="story-icon">💡</div>
                <div>
                  <div class="story-head">The Problem</div>
                  <p>Every semester, students at KCG manually calculated GPA from marksheets, often making errors. Tracking progress across semesters was tedious, and there was no visual way to see academic trends.</p>
                </div>
              </div>
              <div class="story-block">
                <div class="story-icon">🛠️</div>
                <div>
                  <div class="story-head">The Build</div>
                  <p>Built entirely with vanilla HTML, CSS, and JavaScript — no frameworks, just clean code. PDF parsing, grade computation, GPA/CGPA calculations, what-if simulation, and a risk detector all in one file.</p>
                </div>
              </div>
              <div class="story-block">
                <div class="story-icon">🚀</div>
                <div>
                  <div class="story-head">What's Next</div>
                  <p>Backend integration with real PDF AI extraction, class leaderboards with live data, mobile app (React Native), and department-wide analytics for faculty.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="about-footer">Made with ❤️ by Sachin M · KCG College of Technology · 2026</div>
        </main>
      )}

      {/* TOAST NOTIFICATION */}
      {toastShow && <div className="toast-stack"><div className="toast-item">{toastMsg}</div></div>}

      {/* UPLOAD MODAL */}
      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onComplete={handleUploadComplete}
      />

      {/* EDIT MODAL */}
      <EditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        currentSem={currentSem}
        liveData={liveData}
        onSave={handleSaveEdit}
      />
    </div>
  );
}

const dashboardCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Syne:wght@600;700;800&display=swap');

  :root {
    --bg-top: #a8d8f0;
    --bg-bottom: #e4f4ff;
    --bg: #e4f4ff;
    --surface: rgba(255,255,255,0.82);
    --surface2: rgba(255,255,255,0.6);
    --border: rgba(46,125,50,0.18);
    --text: #1a3a1a;
    --text-muted: #2d7a2d;
    --text-light: #88aa88;
    --primary: #2e7d32;
    --primary-light: rgba(46,125,50,0.12);
    --primary-dark: #1b5e20;
    --accent: #2563eb;
    --accent-light: rgba(37,99,235,0.1);
    --warning: #f59e0b;
    --warning-light: #fef3c7;
    --danger: #ef4444;
    --danger-light: #fee2e2;
    --shadow: 0 4px 24px rgba(30,100,30,0.12), 0 1px 4px rgba(30,100,30,0.08);
    --shadow-lg: 0 12px 48px rgba(30,100,30,0.18);
    --radius: 14px;
    --radius-sm: 8px;
    --trans: .3s cubic-bezier(.4,0,.2,1);
    --toggle-bg: rgba(255,255,255,0.65);
    --toggle-border: rgba(255,255,255,0.9);
    --star-opacity: 0;
    --card-border: rgba(255,255,255,0.9);
    --logo-color: #1a7a1a;
    --logo-arc: #2e7d32;
  }

  [data-theme="dark"] {
    --bg-top: #0a1428;
    --bg-bottom: #151d2d;
    --bg: #151d2d;
    --surface: rgba(25,35,55,0.92);
    --surface2: rgba(20,28,45,0.8);
    --border: rgba(148,163,184,0.25);
    --text: #f0f4f8;
    --text-muted: #b8c5d6;
    --text-light: #7a8a9e;
    --primary: #10b981;
    --primary-light: rgba(16,185,129,0.15);
    --primary-dark: #059669;
    --accent: #60a5fa;
    --accent-light: rgba(96,165,250,0.15);
    --warning: #fbbf24;
    --warning-light: rgba(251,191,36,0.2);
    --danger: #ff6b6b;
    --danger-light: rgba(255,107,107,0.2);
    --shadow: 0 4px 24px rgba(0,0,0,0.6), 0 1px 4px rgba(0,0,0,0.4);
    --shadow-lg: 0 12px 48px rgba(0,0,0,0.7);
    --toggle-bg: rgba(255,255,255,0.08);
    --toggle-border: rgba(255,255,255,0.15);
    --star-opacity: 1;
    --card-border: rgba(255,255,255,0.1);
    --logo-color: #93c5fd;
  --logo-arc: #818cf8;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { font-family: 'Outfit', sans-serif; background: linear-gradient(180deg, var(--bg-top) 0%, var(--bg-bottom) 65%); color: var(--text); min-height: 100vh; transition: background 0.7s ease, color var(--trans); font-size: 14px; overflow-x: hidden; }

  .dashboard-page { min-height: 100vh; position: relative; font-family: 'Outfit', sans-serif; background: linear-gradient(180deg, var(--bg-top) 0%, var(--bg-bottom) 65%); transition: background 0.7s ease; }

  /* Stars */
  .stars { position: fixed; inset: 0; pointer-events: none; opacity: var(--star-opacity); transition: opacity 0.7s; z-index: 1; }
  .star { position: absolute; background: #fff; border-radius: 50%; animation: twinkle var(--d, 3s) ease-in-out infinite; animation-delay: var(--dl, 0s); }
  @keyframes twinkle { 0%, 100% { opacity: 0.15; transform: scale(1); } 50% { opacity: 1; transform: scale(1.5); } }

  /* Particles */
  .particles { position: fixed; inset: 0; pointer-events: none; z-index: 1; }
  .particle { position: absolute; border-radius: 50%; animation: float var(--pd, 8s) ease-in-out infinite; animation-delay: var(--pdl, 0s); opacity: 0.4; }
  @keyframes float { 0%, 100% { transform: translateY(0) translateX(0); opacity: 0; } 20%, 80% { opacity: 0.4; } 50% { transform: translateY(-80px) translateX(30px); opacity: 0.6; } }

  /* Clouds */
  .cloud-layer { position: fixed; inset: 0; pointer-events: none; z-index: 1; transition: opacity 0.7s; }
  [data-theme="dark"] .cloud-layer { opacity: 0.04; }
  .cloud { position: absolute; background: rgba(255, 255, 255, 0.85); border-radius: 50px; animation: drift var(--cd, 30s) linear infinite; animation-delay: var(--cdl, 0s); }
  .cloud::before, .cloud::after { content: ''; position: absolute; background: inherit; border-radius: 50%; }
  .cloud::before { width: 60%; height: 200%; top: -80%; left: 15%; }
  .cloud::after { width: 40%; height: 160%; top: -50%; right: 10%; }
  @keyframes drift { from { transform: translateX(-150px); } to { transform: translateX(110vw); } }

  @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes popIn { 0% { opacity: 0; transform: scale(.82); } 60% { transform: scale(1.05); } 100% { opacity: 1; transform: scale(1); } }
  @keyframes pulse-ring { 0%, 100% { box-shadow: 0 0 0 0 rgba(22,163,74,.35); } 60% { box-shadow: 0 0 0 10px rgba(22,163,74,0); } }

  header { background: var(--surface); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-bottom: 1px solid var(--card-border); padding: 0 24px; height: 62px; display: flex; align-items: center; justify-content: space-between; position: sticky; top: 0; z-index: 200; box-shadow: var(--shadow); animation: slideDown .4s ease; transition: background 0.7s ease; flex-wrap: wrap; gap: 8px; }
  .logo { display: flex; align-items: center; gap: 12px; text-decoration: none; }
  .logo-svg-wrap { width: 38px; height: 38px; display: flex; align-items: center; justify-content: center; }
  .logo-svg-wrap svg { width: 100%; height: 100%; }
  .logo-text { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 900; color: var(--text); letter-spacing: -0.6px; line-height: 1; transition: color 0.7s; }
  .logo-text span { color: var(--primary); font-weight: 900; }
  
  /* Logo Area Styles */
  .logo-area { display: flex; flex-direction: column; align-items: center; gap: 0; animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
  .logo-arc { display: inline-block; width: 66px; height: 16px; border-top: 5px solid var(--logo-arc); border-radius: 50% 50% 0 0 / 100% 100% 0 0; margin-bottom: -2px; transition: border-color 0.7s; }
  .logo-txt { font-size: 1.8rem; font-weight: 900; color: var(--logo-color); letter-spacing: -0.5px; line-height: 1; transition: color 0.7s; }
  .logo-txt sup { font-size: 0.38em; vertical-align: super; }
  .tagline { font-size: 0.75rem; font-weight: 800; color: var(--text-muted); letter-spacing: 2px; text-transform: uppercase; opacity: 0.65; transition: color 0.7s; }
  
  .header-right { display: flex; align-items: center; gap: 8px; flex-shrink: 1; min-width: 0; }
  .welcome { font-size: 13px; color: var(--text-muted); margin-right: 4px; }
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; padding: 7px 14px; border-radius: var(--radius-sm); font-size: 13px; font-weight: 600; cursor: pointer; border: none; transition: all .2s; white-space: nowrap; flex-shrink: 0; }
  .btn-text { display: inline; }
  .btn-outline { background: transparent; border: 1.5px solid var(--border); color: var(--text); }
  .btn-outline:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-light); }
  .btn-primary { background: var(--primary); color: #fff; }
  .btn-primary:hover { background: var(--primary-dark); }
  .btn-logout { background: transparent; border: 1.5px solid #ef4444; color: #ef4444; }
  .btn-logout:hover { border-color: #dc2626; color: #fff; background: #ef4444; }
  .theme-toggle { width: 40px; height: 40px; border-radius: 50%; border: 1.5px solid var(--toggle-border); background: var(--toggle-bg); backdrop-filter: blur(12px); color: var(--text); cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 18px; transition: all 0.3s; box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12); }
  .theme-toggle:hover { transform: scale(1.12) rotate(20deg); }

  .hamburger-btn { display: none; flex-direction: column; gap: 5px; background: none; border: none; cursor: pointer; padding: 8px; margin-right: 8px; z-index: 300; flex-shrink: 0; }
  .hamburger-btn span { width: 24px; height: 2.5px; background: var(--text); border-radius: 2px; transition: all .3s; }
  .hamburger-btn.active span:nth-child(1) { transform: rotate(45deg) translate(8px,8px); }
  .hamburger-btn.active span:nth-child(2) { opacity: 0; }
  .hamburger-btn.active span:nth-child(3) { transform: rotate(-45deg) translate(7px,-7px); }

  .header-nav { display: flex; align-items: center; gap: 4px; flex: 1; justify-content: center; }
  .header-nav.mobile-open { position: fixed; top: 62px; left: 0; right: 0; background: var(--surface); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-bottom: 1px solid var(--card-border); flex-direction: column; gap: 0; padding: 8px 0; z-index: 250; display: flex !important; max-height: calc(100vh - 62px); overflow-y: auto; }
  .nav-link { display: inline-flex; align-items: center; gap: 5px; padding: 12px 20px; border-radius: var(--radius-sm); border: none; background: transparent; color: var(--text-muted); font-size: 13px; font-weight: 600; cursor: pointer; transition: all .2s; width: 100%; justify-content: flex-start; }
  .nav-link:hover { background: var(--surface2); color: var(--text); }
  .nav-link.active { background: var(--primary-light); color: var(--primary); }

  main { max-width: 1320px; margin: 0 auto; padding: 24px 20px; display: flex; flex-direction: column; gap: 20px; position: relative; z-index: 10; }
  .grid-main { display: grid; grid-template-columns: 1fr 1.6fr 1fr; gap: 20px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 20px; }
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }

  .card { background: var(--surface); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-radius: var(--radius); padding: 20px; box-shadow: var(--shadow); border: 1px solid var(--card-border); animation: fadeUp .5s ease; transition: background 0.7s ease, border-color 0.7s ease, box-shadow 0.3s ease, transform 0.3s ease; }
  .card:hover { box-shadow: var(--shadow-lg); transform: translateY(-2px); }
  .card-title { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; margin-bottom: 16px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; color: var(--text); }
  .card-badge { font-size: 11px; font-weight: 600; color: var(--text-muted); background: var(--surface2); padding: 3px 9px; border-radius: 12px; border: 1px solid var(--border); }

  .upload-card { background: var(--surface); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-radius: var(--radius); padding: 24px; box-shadow: var(--shadow); border: 1px solid var(--card-border); transition: background 0.7s ease; }
  .upload-inner { display: flex; align-items: flex-start; gap: 24px; }
  .upload-college-img { width: 110px; height: 90px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 40px; }
  .upload-info { flex: 1; }
  .upload-info h2 { font-family: 'Syne', sans-serif; font-size: 19px; font-weight: 700; margin-bottom: 4px; color: var(--text); }
  .upload-info p { color: var(--text-muted); font-size: 13px; margin-bottom: 16px; }
  .upload-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .upload-status { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--primary); background: var(--primary-light); padding: 6px 12px; border-radius: 20px; font-weight: 600; }
  .progress-steps { display: flex; gap: 16px; margin-top: 14px; flex-wrap: wrap; align-items: center; }
  .step { display: flex; align-items: center; gap: 5px; font-size: 12px; color: var(--text-muted); font-weight: 500; }
  .step.done { color: var(--primary); }
  .edit-btn { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: var(--radius-sm); border: 1.5px solid var(--accent); background: var(--accent-light); color: var(--accent); font-size: 13px; font-weight: 600; cursor: pointer; }
  .edit-btn:hover { background: var(--accent); color: white; }

  .student-avatar { width: 70px; height: 70px; border-radius: 12px; background: linear-gradient(135deg, #1e40af, #16a34a); display: flex; align-items: center; justify-content: center; font-size: 30px; margin-bottom: 12px; box-shadow: 0 4px 12px rgba(22,163,74,.2); }
  .student-name { font-family: 'Syne', sans-serif; font-size: 17px; font-weight: 700; margin-bottom: 12px; color: var(--text); }
  .student-info { display: flex; flex-direction: column; gap: 8px; }
  .info-row { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: var(--text-muted); }
  .credits-row { display: flex; gap: 8px; margin-top: 14px; }
  .credit-badge { flex: 1; background: var(--surface2); border-radius: var(--radius-sm); padding: 10px; text-align: center; border: 1px solid var(--border); }
  .credit-badge:hover { border-color: var(--primary); transform: translateY(-2px); }
  .credit-badge .num { font-family: 'Syne', sans-serif; font-size: 19px; font-weight: 800; color: var(--primary); }
  .credit-badge .lbl { font-size: 10px; color: var(--text-muted); margin-top: 2px; }

  .chart-container { position: relative; height: 190px; }
  .chart-legend { display: flex; gap: 16px; margin-top: 10px; }
  .legend-item { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-muted); }
  .legend-dot { width: 10px; height: 10px; border-radius: 50%; }
  .chart-badge { display: inline-flex; align-items: center; gap: 6px; background: var(--primary-light); color: var(--primary); padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; margin-top: 10px; }

  .sem-dropdown-wrap { display: flex; align-items: center; gap: 6px; }
  .sem-label { font-size: 11px; font-weight: 600; color: var(--text-muted); }
  .sem-dropdown { padding: 5px 26px 5px 10px; border-radius: var(--radius-sm); border: 1.5px solid var(--border); background: var(--surface2); color: var(--text); font-size: 12px; font-weight: 600; cursor: pointer; outline: none; transition: border-color .2s; appearance: none; }
  .sem-dropdown:focus { border-color: var(--primary); }

  .subject-list-panel { display: flex; flex-direction: column; gap: 6px; }
  .subj-item { display: flex; align-items: center; gap: 8px; padding: 9px 12px; border-radius: var(--radius-sm); background: var(--surface2); border: 1px solid var(--border); font-size: 12.5px; cursor: default; }
  .subj-item:hover { border-color: var(--accent); transform: translateX(3px); }
  .subj-code { font-weight: 700; color: var(--text-muted); font-size: 11px; width: 38px; }
  .subj-name { flex: 1; font-weight: 500; color: var(--text); }
  .subj-credits { font-size: 11px; color: var(--text-light); }
  .subj-grade { font-weight: 700; font-size: 11px; padding: 2px 7px; border-radius: 4px; }
  .grade-O { background: #dcfce7; color: #166534; }
  .grade-Ap { background: #d1fae5; color: #065f46; }
  .grade-A { background: #dbeafe; color: #1e40af; }
  .grade-Bp { background: #fef3c7; color: #92400e; }
  .grade-B { background: #fed7aa; color: #7c2d12; }
  .grade-C { background: #fee2e2; color: #991b1b; }
  [data-theme="dark"] .grade-O { background: #064e3b; color: #6ee7b7; }
  [data-theme="dark"] .grade-Ap { background: #14532d; color: #a7f3d0; }
  [data-theme="dark"] .grade-A { background: #1e3a8a; color: #93c5fd; }
  [data-theme="dark"] .grade-Bp { background: #78350f; color: #fde047; }
  [data-theme="dark"] .grade-B { background: #5a2f0c; color: #fcd34d; }
  [data-theme="dark"] .grade-C { background: #5f1f1f; color: #fca5a5; }

  .tab-group { display: flex; gap: 3px; background: var(--surface2); border-radius: var(--radius-sm); padding: 3px; border: 1px solid var(--border); }
  .tab { padding: 5px 11px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all .2s; color: var(--text-muted); border: none; background: transparent; }
  .tab.active { background: var(--surface); color: var(--text); }

  .subject-bars { width: 100%; display: flex; flex-direction: column; gap: 10px; }
  .subject-bar-row { display: flex; align-items: center; gap: 10px; }
  .subject-code-lbl { font-size: 11px; font-weight: 700; width: 36px; color: var(--text-muted); }
  .bar-track { flex: 1; height: 18px; background: var(--surface2); border-radius: 4px; overflow: hidden; border: 1px solid var(--border); }
  .bar-fill { height: 100%; display: flex; overflow: hidden; }
  .bar-seg { height: 100%; }
  .seg-green { background: #22c55e; }
  .seg-orange { background: #f59e0b; }
  .seg-red { background: #ef4444; }
  .bar-val { font-size: 11px; font-weight: 700; color: var(--text-muted); width: 28px; text-align: right; }

  .zone-section { margin-bottom: 12px; }
  .zone-label { font-size: 12px; font-weight: 700; margin-bottom: 7px; }
  .zone-label.strong { color: var(--primary); }
  .zone-label.borderline { color: var(--warning); }
  .zone-label.improve { color: var(--danger); }
  .zone-item { display: flex; align-items: center; justify-content: space-between; padding: 8px 12px; border-radius: var(--radius-sm); margin-bottom: 5px; font-size: 12.5px; font-weight: 500; }
  .zone-item.strong { background: var(--primary-light); color: #166534; }
  .zone-item.borderline { background: var(--warning-light); color: #92400e; }
  .zone-item.improve { background: var(--danger-light); color: #991b1b; }
  [data-theme="dark"] .zone-item.strong { background: rgba(16,185,129,0.2); color: #a7f3d0; }
  [data-theme="dark"] .zone-item.borderline { background: rgba(251,191,36,0.15); color: #fde047; }
  [data-theme="dark"] .zone-item.improve { background: rgba(255,107,107,0.15); color: #fca5a5; }
  .zone-grade-badge { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; background: var(--surface); }
  .zone-empty { text-align: center; padding: 16px; color: var(--primary); font-size: 13px; font-weight: 600; background: var(--primary-light); border-radius: var(--radius-sm); }

  .risk-inner { background: var(--surface2); border-radius: var(--radius-sm); padding: 14px; border: 1px solid var(--border); margin-bottom: 12px; }
  .risk-title { font-size: 13px; font-weight: 700; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
  .risk-hint { font-size: 11px; color: var(--text-muted); margin-left: auto; }
  .risk-row { display: flex; align-items: center; gap: 8px; border-radius: var(--radius-sm); padding: 9px 12px; border: 1.5px solid transparent; margin-bottom: 6px; cursor: pointer; }
  .risk-row.danger-row { background: var(--danger-light); border-color: #fecaca; }
  .risk-row.warn-row { background: var(--warning-light); border-color: #fde68a; }
  [data-theme="dark"] .risk-row.danger-row { background: rgba(255,107,107,0.15); border-color: rgba(255,107,107,0.3); }
  [data-theme="dark"] .risk-row.warn-row { background: rgba(251,191,36,0.12); border-color: rgba(251,191,36,0.25); }
  .risk-row:hover { transform: translateX(3px); }
  .risk-sub { font-size: 12px; font-weight: 600; color: var(--danger); flex: 1; }
  .risk-sub.warn-c { color: var(--warning); }
  .risk-grades { display: flex; gap: 4px; }
  .rg { font-size: 10px; font-weight: 700; padding: 2px 6px; border-radius: 4px; }
  .rg-n { background: var(--surface); color: var(--text-muted); }
  .rg-w { background: var(--warning-light); color: #92400e; }
  .rg-d { background: var(--danger-light); color: #991b1b; }
  [data-theme="dark"] .rg-w { background: rgba(251,191,36,0.2); color: #fde047; }
  [data-theme="dark"] .rg-d { background: rgba(255,107,107,0.2); color: #fca5a5; }
  .risk-empty { text-align: center; padding: 14px; color: var(--primary); font-size: 13px; font-weight: 600; background: var(--primary-light); border-radius: var(--radius-sm); }

  .sim-subj-display { display: flex; align-items: center; gap: 8px; padding: 10px 12px; background: var(--surface2); border: 1.5px solid var(--border); border-radius: var(--radius-sm); margin-bottom: 12px; }
  .sim-subj-display.active { border-color: var(--accent); background: var(--accent-light); }
  .sim-pulse { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); }
  .sim-subj-name { font-size: 13px; font-weight: 600; flex: 1; color: var(--text); }
  .sim-from-badge { font-size: 10px; font-weight: 700; padding: 2px 7px; border-radius: 4px; background: var(--border); color: var(--text-muted); }

  .sim-grade-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
  .sim-label { font-size: 11px; font-weight: 600; color: var(--text-muted); }
  .sim-arrow { color: var(--text-muted); font-size: 14px; }
  .sim-to-badge { font-size: 13px; font-weight: 700; color: var(--accent); background: var(--accent-light); padding: 3px 10px; border-radius: 5px; }

  input[type="range"] { width: 100%; height: 8px; border-radius: 4px; margin: 4px 0 10px; cursor: pointer; }
  input[type="range"]::-webkit-slider-thumb { width: 20px; height: 20px; border-radius: 50%; background: var(--accent); border: 2.5px solid white; box-shadow: 0 2px 8px rgba(0,0,0,.2); cursor: pointer; }

  .sim-grades-row { display: flex; justify-content: space-between; font-size: 10px; color: var(--text-light); font-weight: 600; margin-bottom: 6px; }

  .sim-result-box { background: linear-gradient(135deg, var(--primary-light), var(--accent-light)); border-radius: var(--radius-sm); padding: 12px 14px; display: flex; align-items: center; justify-content: space-between; border: 1px solid #bbf7d0; }
  .sim-result-text { font-size: 12px; color: var(--text-muted); }
  .sim-result-gpa { font-family: 'Syne', sans-serif; font-size: 22px; font-weight: 800; color: var(--primary); }
  .sim-delta { font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 4px; margin-left: 6px; }
  .sim-delta.up { background: var(--primary-light); color: var(--primary); }
  .sim-delta.down { background: var(--danger-light); color: var(--danger); }
  .sim-delta.same { background: var(--surface2); color: var(--text-muted); }

  .sim-select-prompt { text-align: center; padding: 20px 14px; color: var(--text-muted); font-size: 13px; border: 1.5px dashed var(--border); border-radius: var(--radius-sm); }
  .prompt-icon { font-size: 28px; margin-bottom: 8px; }

  .grade-chart-container { position: relative; height: 130px; }

  .stats-row { display: flex; gap: 10px; margin-bottom: 14px; }
  .stat-box { flex: 1; background: var(--surface2); border-radius: var(--radius-sm); padding: 12px; border: 1px solid var(--border); text-align: center; }
  .stat-box:hover { border-color: var(--accent); transform: translateY(-2px); }
  .stat-box .stat-num { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 800; color: var(--accent); }
  .stat-box .stat-lbl { font-size: 11px; color: var(--text-muted); margin-top: 2px; }

  .sem-chips { display: flex; gap: 8px; }
  .sem-chip { flex: 1; text-align: center; padding: 10px 4px; border-radius: var(--radius-sm); background: var(--surface2); border: 1.5px solid var(--border); cursor: pointer; user-select: none; }
  .sem-chip:hover { border-color: var(--accent); transform: translateY(-2px); }
  .sem-chip.active { border-color: var(--primary); background: var(--primary-light); color: var(--primary); }
  .sem-chip.active .chip-val { color: var(--primary); }
  .sem-chip.low .chip-val { color: var(--warning); }
  .chip-lbl { font-size: 10px; color: var(--text-muted); margin-bottom: 3px; }
  .chip-val { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 800; color: var(--text); }

  .prog-wrap { margin-top: 14px; }
  .prog-track { height: 8px; background: var(--border); border-radius: 4px; overflow: hidden; }
  .prog-fill { height: 100%; background: linear-gradient(90deg, var(--primary), var(--accent)); border-radius: 4px; transition: width .7s; }
  .prog-labels { font-size: 12px; color: var(--text-muted); margin-top: 6px; display: flex; justify-content: space-between; }
  .trend-up { color: var(--primary); font-weight: 600; }

  .lb-hero { background: linear-gradient(135deg, #16a34a 0%, #2563eb 100%); border-radius: var(--radius); padding: 28px; margin-bottom: 0; box-shadow: var(--shadow-lg); }
  .lb-hero-inner { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
  .lb-trophy { font-size: 52px; }
  .lb-title { font-family: 'Syne', sans-serif; font-size: 26px; font-weight: 800; color: #fff; margin-bottom: 4px; }
  .lb-sub { font-size: 13px; color: rgba(255,255,255,.75); }
  .lb-filters { display: flex; gap: 8px; margin-left: auto; flex-wrap: wrap; }

  .podium-row { display: grid; grid-template-columns: 1fr 1.18fr 1fr; gap: 12px; margin-bottom: 0; align-items: end; }
  .podium-card { background: var(--surface); backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); border-radius: var(--radius); padding: 20px 16px; text-align: center; box-shadow: var(--shadow); border: 1px solid var(--card-border); transition: background 0.7s ease; }
  .podium-card:hover { transform: translateY(-4px); }
  .rank-1 { border-top: 3px solid #f59e0b; padding-top: 24px; }
  .rank-2 { border-top: 3px solid #94a3b8; }
  .rank-3 { border-top: 3px solid #d97706; }
  .podium-medal { font-size: 32px; margin-bottom: 8px; }
  .podium-rank { font-family: 'Syne', sans-serif; font-size: 13px; font-weight: 800; color: var(--text-muted); margin-bottom: 6px; }
  .podium-name { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; color: var(--text); margin-bottom: 2px; }
  .podium-reg { font-size: 11px; color: var(--text-muted); margin-bottom: 8px; }
  .podium-gpa { font-family: 'Syne', sans-serif; font-size: 24px; font-weight: 800; color: var(--primary); margin-bottom: 2px; }
  .podium-lbl { font-size: 10px; color: var(--text-muted); text-transform: uppercase; }
  .rank-1 .podium-gpa { font-size: 28px; color: #f59e0b; }

  .lb-table-card { animation: fadeUp .55s; backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); }
  .lb-search-wrap { margin-bottom: 14px; }
  .lb-search { width: 100%; padding: 9px 14px; border-radius: var(--radius-sm); border: 1.5px solid var(--border); background: var(--surface2); color: var(--text); font-size: 13px; outline: none; }
  .lb-search:focus { border-color: var(--primary); }
  .lb-table { width: 100%; border-collapse: collapse; }
  .lb-table th { font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; padding: 8px 12px; text-align: left; border-bottom: 2px solid var(--border); }
  .lb-table td { padding: 11px 12px; font-size: 13px; border-bottom: 1px solid var(--border); }
  .lb-table tr:hover td { background: var(--surface2); }
  .lb-rank-cell { font-family: 'Syne', sans-serif; font-weight: 800; font-size: 15px; color: var(--text-muted); width: 40px; }
  .lb-me-row td { background: var(--primary-light) !important; }
  .lb-me-badge { font-size: 10px; background: var(--primary); color: #fff; padding: 2px 7px; border-radius: 10px; margin-left: 6px; font-weight: 700; }
  .lb-gpa-cell { font-family: 'Syne', sans-serif; font-weight: 800; font-size: 15px; }
  .lb-trend { font-size: 14px; }
  .lb-bar-wrap { width: 100px; height: 6px; background: var(--border); border-radius: 3px; display: inline-block; overflow: hidden; }
  .lb-bar-fill { height: 100%; background: linear-gradient(90deg, var(--primary), var(--accent)); border-radius: 3px; }

  .my-rank-card { background: linear-gradient(135deg, var(--primary-light), var(--accent-light)); border: 1px solid #bbf7d0; }
  .my-rank-inner { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; }
  .my-rank-left { display: flex; align-items: center; gap: 14px; }
  .my-rank-num { font-family: 'Syne', sans-serif; font-size: 44px; font-weight: 800; color: var(--primary); }
  .my-rank-label { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; color: var(--text); }
  .my-rank-sub { font-size: 12px; color: var(--text-muted); }
  .my-rank-right { display: flex; gap: 20px; }
  .my-rank-stat { display: flex; flex-direction: column; align-items: center; gap: 2px; }
  .my-rank-stat span:first-child { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 800; color: var(--text); }
  .my-rank-stat span:last-child { font-size: 11px; color: var(--text-muted); }

  /* Leaderboard Type Tabs */
  .lb-type-tabs {
    display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap;
    padding: 12px; background: var(--surface); backdrop-filter: blur(12px);
    border-radius: var(--radius); box-shadow: var(--shadow); border: 1px solid var(--card-border);
  }
  .lb-type-tab {
    display: flex; align-items: center; gap: 8px; padding: 10px 18px; border-radius: 10px;
    border: 1.5px solid var(--border); background: transparent; color: var(--text-muted);
    font-size: 13px; font-weight: 600; cursor: pointer; transition: all .25s ease;
  }
  .lb-type-tab:hover {
    border-color: var(--primary); background: var(--primary-light); color: var(--primary);
    transform: translateY(-2px); box-shadow: 0 4px 12px rgba(46,125,50,.1);
  }
  .lb-type-tab.active {
    border-color: var(--primary); background: linear-gradient(135deg, var(--primary), var(--accent));
    color: #fff; box-shadow: 0 6px 20px rgba(46,125,50,.2);
    animation: slideIn .3s ease;
  }
  .tab-icon { font-size: 16px; }
  .tab-label { display: inline; }

  @keyframes slideIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes tableRowFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes podiumBounce { 0% { opacity: 0; transform: scale(0.9) translateY(20px); } 60% { transform: scale(1.05); } 100% { opacity: 1; transform: scale(1) translateY(0); } }

  /* Class Leaderboard Styles */
  .class-hero { background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%); }

  /* Department Leaderboard Styles */
  .dept-hero { background: linear-gradient(135deg, #059669 0%, #06b6d4 100%); }
  .dept-podium-row { display: grid; grid-template-columns: 1fr 1.18fr 1fr; gap: 12px; margin-bottom: 20px; align-items: end; }
  .dept-podium-card {
    background: var(--surface); backdrop-filter: blur(16px); border-radius: var(--radius);
    padding: 20px 16px; text-align: center; box-shadow: var(--shadow); border: 1px solid var(--card-border);
    transition: all .3s ease; animation: podiumBounce .5s ease;
  }
  .dept-podium-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
  .dept-podium-card.rank-1 { border-top: 3px solid #fbbf24; padding-top: 24px; }
  .dept-podium-card.rank-2 { border-top: 3px solid #a78bfa; }
  .dept-podium-card.rank-3 { border-top: 3px solid #f97316; }
  .dept-name { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; color: var(--text); margin-bottom: 2px; }
  .dept-abbr { font-size: 11px; color: var(--text-muted); margin-bottom: 8px; font-weight: 600; }
  .dept-gpa { font-family: 'Syne', sans-serif; font-size: 24px; font-weight: 800; color: var(--primary); margin-bottom: 2px; }
  .dept-podium-card.rank-1 .dept-gpa { font-size: 28px; color: #fbbf24; }
  .dept-lbl { font-size: 10px; color: var(--text-muted); text-transform: uppercase; }
  .dept-sub-info { font-size: 11px; color: var(--text-muted); margin-top: 6px; font-weight: 500; }

  /* College Leaderboard Styles */
  .college-hero { background: linear-gradient(135deg, #dc2626 0%, #f97316 100%); }
  .college-podium-row { display: grid; grid-template-columns: 1fr 1.18fr 1fr; gap: 12px; margin-bottom: 20px; align-items: end; }
  .college-podium-card {
    background: var(--surface); backdrop-filter: blur(16px); border-radius: var(--radius);
    padding: 20px 16px; text-align: center; box-shadow: var(--shadow); border: 1px solid var(--card-border);
    transition: all .3s ease; animation: podiumBounce .5s ease;
  }
  .college-podium-card:hover { transform: translateY(-6px); box-shadow: var(--shadow-lg); }
  .college-podium-card.rank-1 { border-top: 3px solid #f59e0b; padding-top: 24px; }
  .college-podium-card.rank-2 { border-top: 3px solid #a16207; }
  .college-podium-card.rank-3 { border-top: 3px solid #d97706; }
  .college-name { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; color: var(--text); margin-bottom: 2px; }
  .college-abbr { font-size: 11px; color: var(--text-muted); margin-bottom: 8px; font-weight: 600; }
  .college-gpa { font-family: 'Syne', sans-serif; font-size: 24px; font-weight: 800; color: var(--primary); margin-bottom: 2px; }
  .college-podium-card.rank-1 .college-gpa { font-size: 28px; color: #f59e0b; }
  .college-lbl { font-size: 10px; color: var(--text-muted); text-transform: uppercase; }
  .college-sub-info { font-size: 11px; color: var(--text-muted); margin-top: 6px; font-weight: 500; }

  /* Table Row Animations */
  .lb-table-row { color: var(--text); animation: tableRowFadeIn .4s ease; }
  .lb-row-0 { animation-delay: 0.05s; }
  .lb-row-1 { animation-delay: 0.1s; }
  .lb-row-2 { animation-delay: 0.15s; }
  .lb-row-3 { animation-delay: 0.2s; }
  .lb-row-4 { animation-delay: 0.25s; }
  .lb-row-5 { animation-delay: 0.3s; }
  .lb-row-6 { animation-delay: 0.35s; }
  .lb-row-7 { animation-delay: 0.4s; }
  .lb-row-8 { animation-delay: 0.45s; }
  .lb-row-9 { animation-delay: 0.5s; }
  
  .dept-row-0 { animation-delay: 0.05s; }
  .dept-row-1 { animation-delay: 0.1s; }
  .dept-row-2 { animation-delay: 0.15s; }
  .dept-row-3 { animation-delay: 0.2s; }
  .dept-row-4 { animation-delay: 0.25s; }
  
  .college-row-0 { animation-delay: 0.05s; }
  .college-row-1 { animation-delay: 0.1s; }
  .college-row-2 { animation-delay: 0.15s; }
  .college-row-3 { animation-delay: 0.2s; }
  .college-row-4 { animation-delay: 0.25s; }

  /* Table Card Animations */
  .dept-table-card { animation: fadeUp .55s ease; }
  .college-table-card { animation: fadeUp .55s ease; }
  .lb-table tr { transition: all .2s ease; }
  .lb-table tbody tr:hover {
    background: var(--primary-light); transform: translateX(4px);
  }
  .lb-table tbody tr.lb-me-row:hover {
    background: var(--primary-light); transform: translateX(6px); box-shadow: inset 4px 0 0 var(--primary);
  }

  @media (max-width: 999px) {
    .lb-type-tabs { gap: 8px; padding: 10px; }
    .lb-type-tab { padding: 8px 14px; font-size: 12px; gap: 6px; }
    .tab-label { display: none; }
    .dept-podium-row { grid-template-columns: 1fr 1fr; gap: 10px; }
    .college-podium-row { grid-template-columns: 1fr 1fr; gap: 10px; }
  }

  @media (max-width: 767px) {
    .lb-type-tabs { gap: 6px; padding: 8px 10px; }
    .lb-type-tab { padding: 7px 12px; font-size: 11px; }
    .tab-icon { font-size: 14px; }
    .dept-podium-row { grid-template-columns: 1fr; gap: 10px; }
    .college-podium-row { grid-template-columns: 1fr; gap: 10px; }
  }

  .about-main { max-width: 1000px !important; }
  .about-hero { background: var(--surface); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-radius: var(--radius); padding: 32px; box-shadow: var(--shadow); border: 1px solid var(--card-border); display: flex; gap: 28px; align-items: flex-start; flex-wrap: wrap; margin-bottom: 20px; transition: background 0.7s ease; }
  .about-avatar-wrap { position: relative; flex-shrink: 0; }
  .about-avatar { width: 100px; height: 100px; border-radius: 20px; background: linear-gradient(135deg, #1e40af, #16a34a); display: flex; align-items: center; justify-content: center; font-size: 50px; box-shadow: 0 8px 24px rgba(22,163,74,.25); }
  .about-status-dot { position: absolute; bottom: 6px; right: 6px; width: 14px; height: 14px; border-radius: 50%; background: #22c55e; border: 2px solid var(--surface); }
  .about-info { flex: 1; }
  .about-name { font-family: 'Syne', sans-serif; font-size: 24px; font-weight: 800; color: var(--text); margin-bottom: 6px; }
  .about-title-badge { display: inline-block; background: var(--primary-light); color: var(--primary); font-size: 12px; font-weight: 700; padding: 4px 12px; border-radius: 20px; margin-bottom: 12px; border: 1px solid #bbf7d0; }
  .about-bio { font-size: 14px; color: var(--text-muted); line-height: 1.7; margin-bottom: 16px; }
  .about-links { display: flex; gap: 8px; flex-wrap: wrap; }
  .about-link { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border-radius: var(--radius-sm); font-size: 13px; font-weight: 600; cursor: pointer; text-decoration: none; transition: all .2s; }
  .about-link:hover { transform: translateY(-2px); }
  .about-link.github { background: #1a1a2e; color: #fff; }
  .about-link.linkedin { background: #0077b5; color: #fff; }
  .about-link.email { background: #ea4335; color: #fff; }
  .about-link.portfolio { background: linear-gradient(135deg, #7c3aed, #2563eb); color: #fff; }

  .about-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
  .tech-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .tech-item {color: var(--text); display: flex; align-items: center; gap: 8px; padding: 9px 12px; border-radius: var(--radius-sm); background: var(--surface2); border: 1px solid var(--border); font-size: 13px; font-weight: 500; }
  .tech-item:hover { border-color: var(--accent); }
  .tech-icon { font-size: 18px; }
  .about-stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .about-stat { background: var(--surface2); border-radius: var(--radius-sm); padding: 16px; text-align: center; border: 1px solid var(--border); }
  .about-stat-num { font-family: 'Syne', sans-serif; font-size: 26px; font-weight: 800; margin-bottom: 4px; }
  .about-stat-lbl { font-size: 11px; color: var(--text-muted); }
  .about-footer { text-align: center; padding: 24px; font-size: 13px; color: var(--text-muted); }

  /* Story Section */
  .about-story { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  .story-block { background: var(--surface2); border-radius: var(--radius-sm); padding: 16px; border: 1px solid var(--border); display: flex; gap: 12px; transition: all .3s; }
  .story-block:hover { transform: translateY(-4px); border-color: var(--primary); box-shadow: 0 8px 20px rgba(22,163,74,.15); }
  .story-icon { font-size: 32px; min-width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; }
  .story-head { font-family: 'Syne', sans-serif; font-size: 15px; font-weight: 700; margin-bottom: 8px; color: var(--text); }
  .story-block p { font-size: 13px; color: var(--text-muted); line-height: 1.6; }

  .toast-stack { position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%); display: flex; flex-direction: column; align-items: center; gap: 10px; z-index: 999; }
  .toast-item { display: flex; align-items: center; gap: 10px; padding: 12px 22px; border-radius: var(--radius); color: #fff; font-weight: 800; font-size: .92rem; box-shadow: var(--shadow-lg); backdrop-filter: blur(8px); background: #16a34a; }

  @media (max-width: 999px) {
    .hamburger-btn { display: flex; }
    .header-nav { display: none; }
    .header-right { gap: 6px; }
    .welcome { display: none; }
    .btn-outline { padding: 6px 12px; font-size: 12px; }
    .btn { padding: 6px 12px; }
    .grid-main { grid-template-columns: 1fr 1fr; }
    .grid-3 { grid-template-columns: 1fr 1fr; }
    .grid-2 { grid-template-columns: 1fr 1fr; }
    .about-story { grid-template-columns: 1fr 1fr; }
  }

  @media (max-width: 767px) {
    header { padding: 0 14px; height: 56px; gap: 6px; }
    .logo-text { font-size: 16px; }
    .logo-svg-wrap { width: 32px; height: 32px; }
    main { padding: 14px 12px; }
    .grid-2, .grid-3, .grid-main { grid-template-columns: 1fr; }
    .card { padding: 12px; }
    .podium-row { grid-template-columns: 1fr; }
    .upload-inner { flex-direction: column; gap: 12px; }
    .upload-college-img { margin: 0 auto; }
    .entry-row { grid-template-columns: 60px 1fr 40px 52px auto; gap: 5px; }
    .modal { max-width: 95%; margin: 12px; }
    .modal-body { padding: 14px 16px; }
    .modal-header, .modal-footer { padding: 14px 16px; }
    .header-nav.mobile-open { top: 56px; }
    .btn { padding: 6px 10px; font-size: 12px; }
    .btn-text { display: none; }
    .theme-toggle { width: 36px; height: 36px; font-size: 16px; }
    .hamburger-btn { padding: 6px; margin-right: 4px; }
    .hamburger-btn span { width: 20px; height: 2px; }
    .stats-row { flex-wrap: wrap; }
    .stat-box { flex: 0 1 calc(50% - 5px); }
    .sem-chips { flex-wrap: wrap; }
    .sem-chip { flex: 0 1 calc(50% - 4px); }
    .prog-labels { gap: 4px; }
  }

  @media (max-width: 640px) {
    header { padding: 0 12px; gap: 6px; }
    .logo-text { font-size: 14px; }
    .logo-svg-wrap { width: 28px; height: 28px; }
    .header-right { gap: 4px; }
    .btn { padding: 5px 8px; font-size: 11px; }
    .btn-text { display: none; }
    .theme-toggle { width: 32px; height: 32px; font-size: 15px; }
    .hamburger-btn { padding: 5px; margin-right: 2px; }
    .hamburger-btn span { width: 18px; height: 2px; gap: 3px; }
    .chart-container { height: 150px; }
    .about-grid { grid-template-columns: 1fr; }
    .tech-grid { grid-template-columns: 1fr; }
    .about-stats-grid { grid-template-columns: 1fr; }
    .nav-link { padding: 10px 16px; font-size: 12px; }
    .card-title { font-size: 14px; }
    .about-story { grid-template-columns: 1fr; }
    .stats-row { flex-wrap: wrap; }
    .stat-box { flex: 0 1 calc(50% - 5px); }
    .sem-chips { flex-wrap: wrap; }
    .sem-chip { flex: 0 1 calc(50% - 4px); }
  }

  @media (max-width: 480px) {
    header { padding: 0 10px; height: 52px; }
    main { padding: 10px 10px; }
    .logo-text { font-size: 13px; }
    .logo-svg-wrap { width: 24px; height: 24px; }
    .header-right { gap: 3px; }
    .btn { padding: 4px 6px; font-size: 10px; }
    .btn-text { display: none; }
    .theme-toggle { width: 30px; height: 30px; font-size: 14px; }
    .hamburger-btn { padding: 4px; margin-right: 0px; }
    .hamburger-btn span { width: 16px; height: 1.5px; }
    .card { padding: 10px; }
    .student-name { font-size: 15px; }
    .chart-container { height: 120px; }
    .entry-row-header { grid-template-columns: 60px 1fr 40px 45px 30px; gap: 4px; font-size: 10px; }
    .upload-inner { padding: 12px; }
    .nav-link { padding: 8px 14px; font-size: 11px; gap: 4px; }
    .about-story { gap: 12px; }
    .story-block { padding: 12px; }
    .story-icon { font-size: 26px; }
    .story-head { font-size: 13px; }
    .story-block p { font-size: 12px; }
    .stats-row { gap: 6px; }
    .stat-box { flex: 1; padding: 8px; }
    .stat-box .stat-num { font-size: 16px; }
    .stat-box .stat-lbl { font-size: 10px; }
    .sem-chips { gap: 4px; }
    .sem-chip { padding: 8px 2px; font-size: 11px; }
    .chip-lbl { font-size: 9px; }
    .chip-val { font-size: 13px; }
  }

  /* ── Modal base ── */
  @keyframes modalIn { from { opacity: 0; transform: translateY(24px) scale(.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
  @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes popIn   { 0% { opacity: 0; transform: scale(.82); } 60% { transform: scale(1.05); } 100% { opacity: 1; transform: scale(1); } }
  @keyframes glowBorder { 0%,100% { border-color: var(--primary); } 50% { border-color: var(--accent); } }
  @keyframes dropPulse  { 0%,100% { transform: scale(1); } 50% { transform: scale(1.08); } }

  .modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,.55);
    backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
    z-index: 400; display: flex; align-items: center; justify-content: center;
    padding: 16px; animation: fadeIn .2s ease;
  }
  .modal {
    background: var(--surface); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border-radius: var(--radius); width: 100%;
    max-width: 560px; box-shadow: var(--shadow-lg); border: 1px solid var(--card-border);
    animation: modalIn .3s ease; max-height: 90vh; overflow-y: auto;
    transition: background var(--trans);
  }
  .modal-header {
    padding: 20px 24px 16px; border-bottom: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
  }
  .modal-title { font-family: 'Syne', sans-serif; font-size: 17px; font-weight: 700; color: var(--text); }
  .modal-close {
    background: none; border: none; cursor: pointer; color: var(--text-muted);
    font-size: 20px; padding: 2px 6px; border-radius: 6px; line-height: 1; transition: all .2s;
  }
  .modal-close:hover { background: var(--danger-light); color: var(--danger); }
  .modal-body   { padding: 20px 24px; }
  .modal-footer { padding: 14px 24px 20px; border-top: 1px solid var(--border); display: flex; gap: 10px; justify-content: flex-end; }

  /* ── Drop Zone ── */
  .drop-zone {
    border: 2px dashed var(--border); border-radius: var(--radius);
    padding: 36px 24px; text-align: center; cursor: pointer;
    transition: all .25s; background: var(--surface2); position: relative;
  }
  .drop-zone.drag-over { border-color: var(--primary); background: var(--primary-light); animation: glowBorder 1.2s infinite; }
  .drop-zone.drag-over .drop-icon { animation: dropPulse .6s infinite; }
  .drop-icon { font-size: 46px; margin-bottom: 12px; display: block; transition: transform .2s; }
  .drop-title { color: var(--text); font-family: 'Syne', sans-serif; font-size: 17px; font-weight: 700; margin-bottom: 6px; }
  .drop-sub   { color: var(--text); font-size: 13px; color: var(--text-muted); margin-bottom: 16px; }
  .drop-divider { display: flex; align-items: center; gap: 12px; margin: 14px 0; color: var(--text-light); font-size: 12px; }
  .drop-divider::before,.drop-divider::after { content: ''; flex: 1; height: 1px; background: var(--border); }
  .drop-formats { font-size: 11px; color: var(--text-light); margin-top: 12px; }

  /* ── File Preview ── */
  .file-preview { display: none; background: var(--surface2); border: 1.5px solid var(--border); border-radius: var(--radius-sm); padding: 14px 16px; margin-top: 14px; flex-direction: column; gap: 10px; }
  .file-preview.visible { display: flex; animation: popIn .3s ease; }
  .file-meta { display: flex; align-items: center; gap: 12px; }
  .file-icon { font-size: 28px; flex-shrink: 0; }
  .file-details { flex: 1; }
  .file-name { font-weight: 600; font-size: 14px; color: var(--text); }
  .file-size { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
  .file-remove { background: none; border: none; cursor: pointer; color: var(--text-muted); font-size: 18px; padding: 2px 6px; border-radius: 4px; transition: all .2s; }
  .file-remove:hover { background: var(--danger-light); color: var(--danger); }

  /* ── Upload Progress ── */
  .upload-progress-wrap { display: none; margin-top: 14px; }
  .upload-progress-wrap.visible { display: block; }
  .progress-fill-bar { height: 100%; background: linear-gradient(90deg, var(--primary), var(--accent)); border-radius: 3px; transition: width .3s ease; }
  .progress-info { display: flex; justify-content: space-between; font-size: 12px; color: var(--text-muted); margin-top: 6px; }

  /* ── Upload Stages ── */
  .upload-stages { display: flex; gap: 8px; margin-top: 14px; flex-direction: column; }
  .stage-row { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: var(--radius-sm); background: var(--surface2); border: 1px solid var(--border); font-size: 13px; transition: all .3s; }
  .stage-row.active  { border-color: var(--accent); background: var(--accent-light); }
  .stage-row.done    { border-color: var(--primary); background: var(--primary-light); }
  .stage-row.error   { border-color: var(--danger); background: var(--danger-light); }
  .stage-icon { width: 22px; height: 22px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .stage-spinner { width: 16px; height: 16px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin .7s linear infinite; }
  .stage-check { color: var(--primary); font-size: 16px; font-weight: 700; }
  .stage-err   { color: var(--danger);  font-size: 16px; }
  .stage-dot   { width: 8px; height: 8px; border-radius: 50%; background: var(--border); }
  .stage-label { font-weight: 600; color: var(--text); flex: 1; }
  .stage-row.active .stage-label { color: var(--accent); }
  .stage-row.done   .stage-label { color: var(--primary); }
  .stage-row.error  .stage-label { color: var(--danger); }
  .stage-detail { font-size: 11px; color: var(--text-muted); }

  /* ── Upload Complete ── */
  .upload-complete { display: none; text-align: center; padding: 20px; }
  .upload-complete.visible { display: block; animation: popIn .4s ease; }
  .complete-icon  { font-size: 52px; margin-bottom: 12px; }
  .complete-title { font-family: 'Syne', sans-serif; font-size: 18px; font-weight: 700; color: var(--primary); margin-bottom: 6px; }
  .complete-sub   { font-size: 13px; color: var(--text-muted); }
  .complete-stats { display: flex; gap: 10px; margin-top: 16px; justify-content: center; flex-wrap: wrap; }
  .c-stat { background: var(--primary-light); border-radius: var(--radius-sm); padding: 10px 16px; text-align: center; border: 1px solid #bbf7d0; }
  .c-stat .cs-num { font-family: 'Syne', sans-serif; font-size: 20px; font-weight: 800; color: var(--primary); }
  .c-stat .cs-lbl { font-size: 11px; color: var(--text-muted); }

  /* ── Form inputs shared ── */
  .form-group { margin-bottom: 16px; }
  .form-label { font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; display: block; }
  .form-select, .form-input {
    width: 100%; padding: 9px 12px; border-radius: var(--radius-sm);
    border: 1.5px solid var(--border); background: var(--surface2);
    color: var(--text); font-family: 'Outfit', sans-serif; font-size: 14px;
    outline: none; transition: border-color .2s, box-shadow .2s;
  }
  .form-select:focus, .form-input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(22,163,74,.12); }

  /* ── Edit Modal entry rows ── */
  .entry-row-header {
    display: grid; grid-template-columns: 85px 1fr 52px 72px 30px;
    gap: 8px; padding: 0 10px; margin-bottom: 8px;
    font-size: 11px; font-weight: 700; color: var(--text-muted);
    text-transform: uppercase; letter-spacing: .5px;
  }
  .entry-rows-wrap { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }
  .entry-row {
    display: grid; grid-template-columns: 85px 1fr 52px 72px auto;
    gap: 8px; align-items: center; padding: 10px;
    background: var(--surface2); border-radius: var(--radius-sm);
    border: 1px solid var(--border); transition: border-color .2s;
  }
  .entry-row:hover { border-color: var(--accent); }
  .entry-row .form-select, .entry-row .form-input { background: var(--surface); }
  .btn-icon { width: 30px; height: 30px; border-radius: var(--radius-sm); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 700; transition: all .2s; }
  .btn-rem { background: var(--danger-light); color: var(--danger); }
  .btn-rem:hover { background: var(--danger); color: #fff; transform: scale(1.1); }
  .add-row-btn {
    display: flex; align-items: center; justify-content: center; gap: 6px;
    width: 100%; padding: 9px 14px; border-radius: var(--radius-sm);
    border: 1.5px dashed var(--primary); background: transparent;
    color: var(--primary); font-family: 'Outfit', sans-serif; font-size: 13px;
    font-weight: 600; cursor: pointer; transition: all .2s;
  }
  .add-row-btn:hover { background: var(--primary-light); transform: translateY(-1px); }
`;